import React, { Component, Fragment } from 'react';
import { createPortal } from 'react-dom';
import { UserPlus, UserCheck, UserX, Users, Search, Plus, Edit2, Trash2, X, Check, MessageCircle } from 'lucide-react';
import { apiService } from '@/services/api';
import { getUserIdFromToken } from '@/utils/jwt';
import { friendsChatWebSocketService } from '@/services/friendsChatWebSocket';
import { groupChatWebSocketService } from '@/services/groupChatWebSocket';
import Chat from '../Chat/Chat';
import GroupChat from '../GroupChat/GroupChat';
import styles from './Friends.module.scss';

interface Friend {
  id: string;
  userId: string;
  customerProfileId: string;
  firstName: string;
  lastName: string;
  fullName: string;
  email: string;
  avatar?: string;
  profilePicture?: string;
  skillLevel?: string;
  status: 'pending' | 'accepted' | 'rejected' | 'cancelled' | 'sent';
  createdAt: string;
  updatedAt?: string;
  requestMessage?: string; // Original message when request was sent
  acceptComment?: string; // Comment when request was accepted
  rejectComment?: string; // Comment when request was rejected
  groupIds?: string[];
  unreadCount?: number; // Number of unread messages from this friend
}

interface FriendGroup {
  id: string;
  name: string;
  description?: string;
  memberCount: number;
  color?: string;
  profilePicUrl?: string;
  profile_pic_url?: string;
  avatarUrl?: string; // API returns avatarUrl
  createdBy?: string;
  creator?: {
    id: string;
    userId: string;
    displayName: string;
    profilePic?: string;
  };
  createdAt: string;
  updatedAt?: string;
}

interface Player {
  id: string;
  userId: string;
  customerProfileId: string;
  firstName: string;
  lastName: string;
  displayName: string;
  tagline?: string;
  email: string;
  avatar?: string;
  profilePicture?: string;
  skillLevel?: string;
  rating?: number;
  profileMode: 'public' | 'private' | 'privacy';
  city?: string;
  state?: string;
  country?: string;
  preferredGameTypes?: string[];
}

interface FriendsState {
  activeTab: 'players' | 'requests' | 'friends' | 'groups';
  searchTerm: string;
  players: Player[];
  friends: Friend[];
  friendRequests: Friend[];
  sentRequests: Friend[];
  groups: FriendGroup[];
  selectedGroup: FriendGroup | null;
  showCreateGroupModal: boolean;
  showAddFriendModal: boolean;
  showAcceptModal: boolean;
  showRejectModal: boolean;
  selectedPlayer: Player | null;
  selectedRequest: Friend | null;
  friendRequestComment: string;
  acceptComment: string;
  rejectComment: string;
  newGroupName: string;
  newGroupDescription: string;
  newGroupColor: string;
  newGroupProfilePic: File | null; // Profile picture file for new group
  newGroupProfilePicPreview: string | null; // Preview URL for profile picture
  selectedFriendsForGroup: string[]; // Friend IDs selected for group creation
  loading: boolean;
  showChat: boolean;
  selectedFriendForChat: Friend | null;
  selectedGroupForChat: FriendGroup | null; // Currently open group for posts/chat
  showGroupPosts: boolean; // Whether group posts view is open
  showGroupChat: boolean; // Whether group chat is open
  currentUserId: string | null;
  currentUserProfileId: string | null;
  unreadCounts: Record<string, number>; // Map of friendId -> unreadCount
  groupUnreadCounts: Record<string, number>; // Map of groupId -> unreadCount
  unreadRefreshInterval: NodeJS.Timeout | null;
  isFriendTyping: boolean; // Whether friend is typing
  isTyping: boolean; // Whether current user is typing
  groupInvitations: any[]; // Pending group invitations
  groupMembers: Record<string, any[]>; // Map of groupId -> members
  userRoleInGroup: Record<string, 'admin' | 'moderator' | 'member'>; // Map of groupId -> user role
  showAddMembersModal: boolean;
  selectedGroupForAddMembers: FriendGroup | null;
  selectedFriendIdsToAdd: string[]; // Profile IDs to add to group
}

class Friends extends Component<{}, FriendsState> {
  private chatRef: React.RefObject<any> = React.createRef();
  private groupChatRef: React.RefObject<any> = React.createRef();
  private typingTimeout: NodeJS.Timeout | null = null;
  
  constructor(props: {}) {
    super(props);
    this.state = {
      activeTab: 'players',
      searchTerm: '',
      players: [],
      friends: [],
      friendRequests: [],
      sentRequests: [],
      groups: [],
      selectedGroup: null,
      showCreateGroupModal: false,
      showAddFriendModal: false,
      showAcceptModal: false,
      showRejectModal: false,
      selectedPlayer: null,
      selectedRequest: null,
      friendRequestComment: '',
      acceptComment: '',
      rejectComment: '',
      newGroupName: '',
      newGroupDescription: '',
      newGroupColor: '#3b82f6',
      newGroupProfilePic: null,
      newGroupProfilePicPreview: null,
      selectedFriendsForGroup: [],
      loading: true,
      showChat: false,
      selectedFriendForChat: null,
      selectedGroupForChat: null,
      showGroupPosts: false,
      showGroupChat: false,
      currentUserId: null,
      currentUserProfileId: null,
      unreadCounts: {},
      groupUnreadCounts: {},
      unreadRefreshInterval: null,
      isFriendTyping: false,
      isTyping: false,
      groupInvitations: [],
      groupMembers: {},
      userRoleInGroup: {},
      showAddMembersModal: false,
      selectedGroupForAddMembers: null,
      selectedFriendIdsToAdd: []
    };
  }

  async componentDidMount() {
    try {
      // Get current user ID from token
      const { accessToken } = apiService.getTokens();
      const userId = getUserIdFromToken(accessToken);
      
      // Get current user profile to get profile ID
      let profileId: string | null = null;
      try {
        const profileResponse = await apiService.getCustomerProfile();
        if (profileResponse.success && profileResponse.data) {
          profileId = profileResponse.data.id || null;
        }
      } catch (error) {
        console.error('[Friends] Failed to get profile:', error);
      }
      
      this.setState({ 
        currentUserId: userId || null,
        currentUserProfileId: profileId || null
      });
      
      await this.loadFriendsData();
      await this.loadUnreadCounts();
      this.tryOpenConversationFromUrl();
      
      // Set up periodic refresh of unread counts (every 30 seconds)
      const interval = setInterval(() => {
        this.loadUnreadCounts();
      }, 30000);
      this.setState({ unreadRefreshInterval: interval });
      
      // Connect to Socket.IO for real-time updates
      this.setupSocketListeners();
    } catch (error) {
      console.error('[Friends] Component mount error:', error);
      this.setState({ loading: false });
    }
  }

  private searchTimeout: NodeJS.Timeout | null = null;

  componentDidUpdate(prevProps: {}, prevState: FriendsState) {
    // Lock body scroll when chat overlay is open
    if (prevState.showChat !== this.state.showChat) {
      document.body.style.overflow = this.state.showChat ? 'hidden' : '';
    }
    // Reload players when search term changes (debounced search)
    if (prevState.searchTerm !== this.state.searchTerm && this.state.activeTab === 'players') {
      // Clear previous timeout
      if (this.searchTimeout) {
        clearTimeout(this.searchTimeout);
      }
      // Debounce search - reload after 500ms of no typing
      this.searchTimeout = setTimeout(() => {
        this.loadPlayers();
      }, 500);
    }
  }

  componentWillUnmount() {
    // Restore body scroll if chat was open
    if (this.state.showChat) {
      document.body.style.overflow = '';
    }
    // Cleanup timeout on unmount
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout);
    }
    // Clear the unread count refresh interval
    if (this.state.unreadRefreshInterval) {
      clearInterval(this.state.unreadRefreshInterval);
    }
    // Disconnect Socket.IO
    this.removeSocketListeners();
  }

  setupSocketListeners = () => {
    console.log('[Friends] 🔌 Setting up Socket.IO listeners...');
    
    // Connect to Socket.IO first
    friendsChatWebSocketService.connect();
    groupChatWebSocketService.connect();

    // Set up event listeners immediately - they will work once socket connects
    // The service's internal event system will forward events when socket is ready
    
    // Friend Request Events
    friendsChatWebSocketService.on('friend_request:sent', this.handleFriendRequestSent);
    friendsChatWebSocketService.on('friend_request:received', this.handleFriendRequestReceived);
    friendsChatWebSocketService.on('friend_request:accepted', this.handleFriendRequestAccepted);
    friendsChatWebSocketService.on('friend_request:accepted_confirmation', this.handleFriendRequestAcceptedConfirmation);
    friendsChatWebSocketService.on('friend_request:rejected', this.handleFriendRequestRejected);
    friendsChatWebSocketService.on('friend_request:cancelled', this.handleFriendRequestCancelled);

    // Chat Messaging Events - These are critical for real-time updates
    // These MUST be registered for real-time updates to work
    console.log('[Friends] 📨 Registering message:new handler for receiving messages');
    friendsChatWebSocketService.on('message:new', this.handleNewMessage);
    console.log('[Friends] 📤 Registering message:sent handler for sent message confirmations');
    friendsChatWebSocketService.on('message:sent', this.handleMessageSent);
    friendsChatWebSocketService.on('messages:read', this.handleMessagesRead);
    
    // Chat Room Events
    friendsChatWebSocketService.on('chat:joined', (data: any) => {
      console.log('[Friends] ✅ Joined chat room:', data);
    });
    
    // Connection event - log when socket connects
    friendsChatWebSocketService.on('connect', (data: any) => {
      console.log('[Friends] ✅✅✅ Socket.IO connected! Socket ID:', data?.socketId);
      console.log('[Friends] Socket connection status:', friendsChatWebSocketService.isConnected());
    });
    
    // Reconnection event - rejoin chat room if chat is open
    friendsChatWebSocketService.on('reconnected', () => {
      console.log('[Friends] 🔄 Socket reconnected, rejoining chat room if needed');
      if (this.state.selectedFriendForChat?.userId && friendsChatWebSocketService.isConnected()) {
        friendsChatWebSocketService.joinChatRoom(this.state.selectedFriendForChat.userId);
      }
    });
    
    // Typing Indicator Events (updated event name)
    friendsChatWebSocketService.on('chat:typing', this.handleFriendTyping);
    
    // Group Chat Events
    groupChatWebSocketService.on('group:created', this.handleGroupCreated);
    // Note: group:message:new is handled by GroupChat component when chat is open
    // We only handle notifications here when chat is closed
    groupChatWebSocketService.on('group:message:notification', this.handleGroupMessageNotification);
    groupChatWebSocketService.on('group:comment:new', this.handleGroupCommentNew);
    groupChatWebSocketService.on('group:comment:sentiment:updated', this.handleGroupCommentSentimentUpdated);
    groupChatWebSocketService.on('group:comment:sentiment:removed', this.handleGroupCommentSentimentRemoved);
    
    // Log initial connection status
    console.log('[Friends] Initial socket connection status:', friendsChatWebSocketService.isConnected());
    
    // Check connection status after a short delay (socket connection is async)
    setTimeout(() => {
      const isConnected = friendsChatWebSocketService.isConnected();
      console.log('[Friends] Socket connection status after 1s:', isConnected);
      if (!isConnected) {
        console.warn('[Friends] ⚠️ Socket not connected yet. Events may not work until connection is established.');
      }
    }, 1000);
  };

  removeSocketListeners = () => {
    // Remove all event listeners
    friendsChatWebSocketService.off('friend_request:sent', this.handleFriendRequestSent);
    friendsChatWebSocketService.off('friend_request:received', this.handleFriendRequestReceived);
    friendsChatWebSocketService.off('friend_request:accepted', this.handleFriendRequestAccepted);
    friendsChatWebSocketService.off('friend_request:accepted_confirmation', this.handleFriendRequestAcceptedConfirmation);
    friendsChatWebSocketService.off('friend_request:rejected', this.handleFriendRequestRejected);
    friendsChatWebSocketService.off('friend_request:cancelled', this.handleFriendRequestCancelled);
    friendsChatWebSocketService.off('message:new', this.handleNewMessage);
    friendsChatWebSocketService.off('message:sent', this.handleMessageSent);
    friendsChatWebSocketService.off('messages:read', this.handleMessagesRead);
    friendsChatWebSocketService.off('chat:typing', this.handleFriendTyping);
    friendsChatWebSocketService.off('chat:joined', () => {});
    friendsChatWebSocketService.off('reconnected', () => {});
    
    // Remove group chat event listeners
    groupChatWebSocketService.off('group:created', this.handleGroupCreated);
    // Note: group:message:new is handled by GroupChat component
    groupChatWebSocketService.off('group:message:notification', this.handleGroupMessageNotification);
    groupChatWebSocketService.off('group:comment:new', this.handleGroupCommentNew);
    groupChatWebSocketService.off('group:comment:sentiment:updated', this.handleGroupCommentSentimentUpdated);
    groupChatWebSocketService.off('group:comment:sentiment:removed', this.handleGroupCommentSentimentRemoved);
    groupChatWebSocketService.off('connect', () => {});
    
    // Leave chat room if chat is open
    if (this.state.selectedFriendForChat?.userId) {
      friendsChatWebSocketService.leaveChatRoom(this.state.selectedFriendForChat.userId);
    }
    
    // Leave group room if group chat is open
    if (this.state.selectedGroupForChat?.id) {
      groupChatWebSocketService.leaveGroupRoom(this.state.selectedGroupForChat.id);
    }
    
    // Clear typing timeout
    if (this.typingTimeout) {
      clearTimeout(this.typingTimeout);
    }
    
    // Disconnect Socket.IO
    friendsChatWebSocketService.disconnect();
    groupChatWebSocketService.disconnect();
  };

  // Friend Request Socket Event Handlers
  handleFriendRequestSent = (data: any) => {
    console.log('[Friends] Friend request sent via socket:', data);
    // Reload friend requests to show the new sent request
    this.loadFriendsData();
  };

  handleFriendRequestReceived = (data: any) => {
    console.log('[Friends] Friend request received via socket:', data);
    // Show notification
    if (data.requester?.displayName) {
      // You can add a toast notification here
      console.log(`New friend request from ${data.requester.displayName}`);
    }
    // Reload friend requests to show the new received request
    this.loadFriendsData();
    // Refresh unread counts
    this.loadUnreadCounts();
  };

  handleFriendRequestAccepted = (data: any) => {
    console.log('[Friends] Friend request accepted via socket:', data);
    // Show success notification
    if (data.recipient?.displayName) {
      console.log(`${data.recipient.displayName} accepted your friend request!`);
    }
    // Reload friends and requests
    this.loadFriendsData();
  };

  handleFriendRequestAcceptedConfirmation = (data: any) => {
    console.log('[Friends] Friend request accepted confirmation via socket:', data);
    // Reload friends and requests
    this.loadFriendsData();
  };

  handleFriendRequestRejected = (data: any) => {
    console.log('[Friends] Friend request rejected via socket:', data);
    // Reload friend requests to remove the rejected request
    this.loadFriendsData();
  };

  handleFriendRequestCancelled = (data: any) => {
    console.log('[Friends] Friend request cancelled via socket:', data);
    // Reload friend requests to remove the cancelled request
    this.loadFriendsData();
    // Refresh unread counts
    this.loadUnreadCounts();
  };

  // Chat Messaging Socket Event Handlers
  handleNewMessage = (message: any) => {
    console.log('[Friends] ✅✅✅ handleNewMessage called - New message received via socket:', message);
    console.log('[Friends] Message senderId:', message.senderId);
    console.log('[Friends] Message recipientId:', message.recipientId);
    console.log('[Friends] Current user profile ID:', this.state.currentUserProfileId);
    
    // Check if I am the recipient
    const isRecipient = message.recipientId === this.state.currentUserProfileId;
    
    if (!isRecipient) {
      console.log('[Friends] ⚠️ Message is not for me, ignoring');
      return;
    }
    
    console.log('[Friends] ✅ I am the recipient of this message');
    console.log('[Friends] Is chat open?', this.state.showChat);
    console.log('[Friends] Selected friend ID:', this.state.selectedFriendForChat?.id);
    
    // Check if chat is open for this sender
    const selectedFriend = this.state.selectedFriendForChat;
    const isChatOpen = this.state.showChat && selectedFriend !== null;
    const isForCurrentChat = selectedFriend && (
      selectedFriend.id === message.senderId || 
      selectedFriend.userId === message.sender?.userId ||
      selectedFriend.customerProfileId === message.senderId
    );
    
    // When a new message is received, add it directly to the chat without showing loading screen
    // This provides instant feedback without interrupting the user experience
    if (isChatOpen && isForCurrentChat) {
      console.log('[Friends] ✅ Chat is open for this sender - Adding message directly without loading screen');
      // Chat is open for this friend, add the message directly using addMessage
      // This avoids showing the loading screen and provides instant updates
      if (this.chatRef.current && this.chatRef.current.addMessage) {
        // Map socket message to Chat Message format
        const chatMessage: any = {
          id: message.id,
          senderId: message.senderId,
          recipientId: message.recipientId,
          messageText: message.messageText,
          replyToMessage: message.replyToMessage,
          isRead: false, // Will be marked as read when we reload or mark as read
          readAt: message.readAt,
          attachments: message.attachments || [],
          createdAt: message.createdAt,
          updatedAt: message.updatedAt,
          sender: message.sender,
          recipient: message.recipient
        };
        
        console.log('[Friends] ✅ Adding received message directly to chat via addMessage');
        this.chatRef.current.addMessage(chatMessage);
        
        // Mark the message as read in the background (without showing loading)
        // This ensures the sender sees the read receipt
        if (this.handleMarkAsRead) {
          this.handleMarkAsRead(message.senderId, [message.id]).catch(err => {
            console.error('[Friends] Failed to mark message as read:', err);
          });
        }
      } else {
        console.log('[Friends] ⚠️ Chat ref or addMessage not available, using handleLoadMessages as fallback');
        // Fallback: call handleLoadMessages (this will show loading, but it's a fallback)
        this.handleLoadMessages(message.senderId, true);
      }
    } else {
      console.log('[Friends] Chat is NOT open for this sender - Updating unread count');
      // Chat is not open, update unread count
      this.setState(prevState => ({
        unreadCounts: {
          ...prevState.unreadCounts,
          [message.senderId]: (prevState.unreadCounts[message.senderId] || 0) + 1
        }
      }));
      
      // Show notification
      if (message.sender?.displayName) {
        console.log(`📬 New message from ${message.sender.displayName}: ${message.messageText}`);
      }
      
      // Refresh unread counts from API to ensure accuracy
      this.loadUnreadCounts();
      
      // If chat gets opened later, reload messages to ensure we have the latest
      setTimeout(() => {
        if (this.state.selectedFriendForChat?.id === message.senderId && this.handleLoadMessages) {
          console.log('[Friends] 🔄 Chat was opened after receiving message, reloading to ensure message is shown with markAsRead=true');
          this.handleLoadMessages(message.senderId, true);
        }
      }, 2000);
    }
  };

  handleMessageSent = (message: any) => {
    console.log('[Friends] ✅✅✅ handleMessageSent called - Message sent confirmation via socket:', message);
    console.log('[Friends] Current selected friend:', this.state.selectedFriendForChat?.id);
    console.log('[Friends] Message recipientId:', message.recipientId);
    console.log('[Friends] Message senderId:', message.senderId);
    console.log('[Friends] Current user profile ID:', this.state.currentUserProfileId);
    
    // When you send a message, you receive 'message:sent' event
    // The message.senderId should be your profile ID, and message.recipientId should be the friend's ID
    // This event is for the SENDER to see their own message immediately
    // Check if this message is for the currently open chat
    const isCurrentChat = this.state.selectedFriendForChat?.id === message.recipientId;
    
    // Map socket message to Chat Message format
    const chatMessage: any = {
      id: message.id,
      senderId: message.senderId,
      recipientId: message.recipientId,
      messageText: message.messageText,
      replyToMessage: message.replyToMessage,
      isRead: message.isRead || false,
      readAt: message.readAt,
      attachments: message.attachments || [],
      createdAt: message.createdAt,
      updatedAt: message.updatedAt,
      sender: message.sender,
      recipient: message.recipient
    };
    
    if (isCurrentChat) {
      console.log('[Friends] ✅ Chat is open, adding sent message to chat via socket event');
      // Chat is open, add message immediately via ref (real-time from socket)
      if (this.chatRef.current && this.chatRef.current.addMessage) {
        console.log('[Friends] Calling addMessage on chat ref with socket message');
        this.chatRef.current.addMessage(chatMessage);
        console.log('[Friends] ✅ Message added to chat from socket event');
      } else {
        // Fallback: reload messages
        console.log('[Friends] ⚠️ Chat ref not available, reloading messages as fallback');
        if (this.handleLoadMessages && message.recipientId) {
          this.handleLoadMessages(message.recipientId);
        }
      }
    } else {
      console.log('[Friends] Chat is NOT open, but message was sent. Will appear when chat is opened.');
      // Chat is not open, but message was sent
      // When user opens this chat later, messages will be loaded from API
      // No need to do anything here, just log
    }
  };

  handleMessagesRead = (data: any) => {
    console.log('[Friends] Messages read via socket:', data);
    
    // If chat is open for this friend, update read status
    if (this.state.selectedFriendForChat?.id === data.friendId) {
      if (this.chatRef.current && this.chatRef.current.updateMessageReadStatus) {
        // We need to get message IDs from the conversation
        // For now, reload messages to get updated read status
        this.handleLoadMessages(data.friendId);
      } else {
        this.handleLoadMessages(data.friendId);
      }
    }
    
    // Update unread count
    this.setState(prevState => ({
      unreadCounts: {
        ...prevState.unreadCounts,
        [data.friendId]: 0
      }
    }));
  };

  handleTyping = (isTyping: boolean) => {
    const { selectedFriendForChat } = this.state;
    
    // Update local typing state
    this.setState({ isTyping });
    
    // Use friend's userId (not profile ID) for typing events
    if (selectedFriendForChat?.userId && friendsChatWebSocketService.isConnected()) {
      if (isTyping) {
        friendsChatWebSocketService.startTyping(selectedFriendForChat.userId);
      } else {
        friendsChatWebSocketService.stopTyping(selectedFriendForChat.userId);
      }
    }
  };

  handleFriendTyping = (data: any) => {
    console.log('[Friends] Friend typing event received:', data);
    const { selectedFriendForChat } = this.state;
    
    // Check if this typing event is for the currently open chat
    // data.friendId is the friend's user ID
    if (selectedFriendForChat?.userId === data.friendId) {
      console.log('[Friends] Updating typing indicator for current chat');
      this.setState({ isFriendTyping: data.isTyping });
      
      // Clear typing indicator after 5 seconds if still typing (fallback)
      if (this.typingTimeout) {
        clearTimeout(this.typingTimeout);
      }
      
      if (data.isTyping) {
        // Auto-hide typing indicator after 5 seconds (in case stop event is missed)
        this.typingTimeout = setTimeout(() => {
          this.setState({ isFriendTyping: false });
        }, 5000);
      }
    }
  };

  loadPlayers = async () => {
    try {
      const playersRes = await apiService.getPublicPlayers({
        search: this.state.searchTerm || undefined,
        page: 1,
        limit: 500 // Increased limit to get more players
      }).catch(() => ({ success: false, data: [] }));
      
      // Extract players array from API response
      // API response structure: { players: [...], pagination: {...} }
      const playersArray = (playersRes.data && typeof playersRes.data === 'object' && !Array.isArray(playersRes.data) && 'players' in playersRes.data)
        ? (playersRes.data as any).players
        : (Array.isArray(playersRes.data) ? playersRes.data : []);
      
      if (playersRes.success && playersArray && Array.isArray(playersArray) && playersArray.length > 0) {
        // Map API response to Player interface
        // Filter out any invalid entries and map valid ones
        const mappedPlayers: Player[] = [];
        
        for (const player of playersArray) {
          if (!player) continue;
          
          // Get userId and id - use one as fallback for the other if needed
          const userId = player.userId || player.user?.id || player.id || player.customerProfileId;
          const id = player.id || player.customerProfileId || userId;
          
          // Only skip if we have absolutely no identifier
          if (!id && !userId) {
            console.warn('[Friends] Skipping player with no identifier:', player);
            continue;
          }
          
          // Ensure both are set (use one as fallback for the other)
          const finalUserId = userId || id;
          const finalId = id || userId;
          
          mappedPlayers.push({
            id: finalId,
            userId: finalUserId,
            customerProfileId: player.customerProfileId || id,
            firstName: player.firstName || player.first_name || '',
            lastName: player.lastName || player.last_name || '',
            displayName: player.displayName || player.display_name || `${player.firstName || player.first_name || ''} ${player.lastName || player.last_name || ''}`.trim() || 'Player',
            tagline: player.tagline || '',
            email: player.email || '',
            avatar: player.avatar || player.profilePicture || player.profilePic || player.profile_pic,
            profilePicture: player.profilePicture || player.profilePic || player.profile_pic,
            skillLevel: player.skillLevel || player.skill_level,
            rating: player.rating,
            profileMode: (player.profileMode || player.profile_mode || 'public').toLowerCase() as 'public' | 'private' | 'privacy',
            city: player.city,
            state: player.state,
            country: player.country,
            preferredGameTypes: Array.isArray(player.preferredGameTypes) 
              ? player.preferredGameTypes 
              : Array.isArray(player.preferred_game_types) 
                ? player.preferred_game_types 
                : []
          });
        }
        
        console.log(`[Friends] Loaded ${mappedPlayers.length} players from API (${playersArray.length} total in response)`);
        if (mappedPlayers.length < playersArray.length) {
          console.warn(`[Friends] Some players were filtered out. Expected ${playersArray.length}, got ${mappedPlayers.length}`);
        }
        console.log(`[Friends] Setting players state to ${mappedPlayers.length} players`);
        console.log(`[Friends] First 5 players:`, mappedPlayers.slice(0, 5).map(p => ({ id: p.id, userId: p.userId, displayName: p.displayName })));
        this.setState({ players: mappedPlayers }, () => {
          console.log(`[Friends] State updated. Current players count: ${this.state.players.length}`);
          console.log(`[Friends] getFilteredPlayers() will return: ${this.getFilteredPlayers().length} players`);
        });
      } else {
        this.setState({ players: [] });
      }
    } catch (error) {
      console.error('[Friends] Failed to load players:', error);
      this.setState({ players: [] });
    }
  };

  /** Open conversation from URL (e.g. FCM: /customer/friends?conversation=...). Call after loadFriendsData. */
  tryOpenConversationFromUrl = () => {
    if (typeof window === 'undefined') return;
    const params = new URLSearchParams(window.location.search);
    const conversation = params.get('conversation');
    if (!conversation || !conversation.trim()) return;
    const convId = conversation.trim();
    const groups = this.state.groups || [];
    const friends = this.state.friends || [];
    const uuidLike = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
    if (uuidLike.test(convId)) {
      const group = groups.find((g: any) => g.id === convId);
      if (group) {
        this.handleOpenGroupChat(group);
        return;
      }
    }
    if (convId.startsWith('dm:') || convId.includes(':')) {
      const parts = convId.split(':');
      const otherId = parts.length >= 2 ? (parts[1] === this.state.currentUserProfileId ? parts[2] : parts[1]) : convId;
      const friend = friends.find((f: any) => f.id === otherId || f.userId === otherId || f.customerProfileId === otherId);
      if (friend) {
        this.handleOpenChat(friend);
      }
    }
  };

  loadFriendsData = async () => {
    try {
      this.setState({ loading: true });
      
      // Load players (public profiles only)
      const playersRes = await apiService.getPublicPlayers({
        search: this.state.searchTerm || undefined,
        page: 1,
        limit: 500 // Increased limit to get more players
      }).catch(() => ({ success: false, data: [] }));

      // Load friends, requests, groups, and group invitations
      const [friendsRes, requestsRes, groupsRes, invitationsRes] = await Promise.all([
        apiService.getFriends().catch(() => ({ success: false, data: [] })),
        apiService.getFriendRequests().catch(() => ({ success: false, data: { received: [], sent: [] } })),
        apiService.getGroups().catch(() => ({ success: false, data: [] })),
        apiService.getPendingInvitations().catch(() => ({ success: false, data: [] }))
      ]);

      const playersData = playersRes.data;
      console.log('playersData', playersData);

      // Extract players array from API response
      // API response structure: { players: [...], pagination: {...} }
      const playersArray = (playersData && typeof playersData === 'object' && !Array.isArray(playersData) && 'players' in playersData)
        ? (playersData as any).players
        : (Array.isArray(playersData) ? playersData : []);

      if (playersRes.success && playersArray && Array.isArray(playersArray) && playersArray.length > 0) {
        // Map API response to Player interface
        // Filter out any invalid entries and map valid ones
        const mappedPlayers: Player[] = [];
        
        for (const player of playersArray) {
          if (!player) continue;
          
          // Get userId and id - use one as fallback for the other if needed
          const userId = player.userId || player.user?.id || player.id || player.customerProfileId;
          const id = player.id || player.customerProfileId || userId;
          
          // Only skip if we have absolutely no identifier
          if (!id && !userId) {
            console.warn('[Friends] Skipping player with no identifier:', player);
            continue;
          }
          
          // Ensure both are set (use one as fallback for the other)
          const finalUserId = userId || id;
          const finalId = id || userId;
          
          mappedPlayers.push({
            id: finalId,
            userId: finalUserId,
            customerProfileId: player.customerProfileId || id,
            firstName: player.firstName || player.first_name || '',
            lastName: player.lastName || player.last_name || '',
            displayName: player.displayName || player.display_name || `${player.firstName || player.first_name || ''} ${player.lastName || player.last_name || ''}`.trim() || 'Player',
            tagline: player.tagline || '',
            email: player.email || '',
            avatar: player.avatar || player.profilePicture || player.profilePic || player.profile_pic,
            profilePicture: player.profilePicture || player.profilePic || player.profile_pic,
            skillLevel: player.skillLevel || player.skill_level,
            rating: player.rating,
            profileMode: (player.profileMode || player.profile_mode || 'public').toLowerCase() as 'public' | 'private' | 'privacy',
            city: player.city,
            state: player.state,
            country: player.country,
            preferredGameTypes: Array.isArray(player.preferredGameTypes) 
              ? player.preferredGameTypes 
              : Array.isArray(player.preferred_game_types) 
                ? player.preferred_game_types 
                : []
          });
        }
        
        console.log(`[Friends] Loaded ${mappedPlayers.length} players from API (${playersArray.length} total in response)`);
        if (mappedPlayers.length < playersArray.length) {
          console.warn(`[Friends] Some players were filtered out. Expected ${playersArray.length}, got ${mappedPlayers.length}`);
        }
        console.log(`[Friends] Setting players state to ${mappedPlayers.length} players`);
        console.log(`[Friends] First 5 players:`, mappedPlayers.slice(0, 5).map(p => ({ id: p.id, userId: p.userId, displayName: p.displayName })));
        this.setState({ players: mappedPlayers }, () => {
          console.log(`[Friends] State updated. Current players count: ${this.state.players.length}`);
          console.log(`[Friends] getFilteredPlayers() will return: ${this.getFilteredPlayers().length} players`);
        });
      } else {
        this.setState({ players: [] });
      }

      if (friendsRes.success && friendsRes.data) {
        // Extract friends array from API response
        // API response structure: { friends: [...], count: number }
        const friendsArray = (friendsRes.data && typeof friendsRes.data === 'object' && !Array.isArray(friendsRes.data) && 'friends' in friendsRes.data)
          ? (friendsRes.data as any).friends
          : (Array.isArray(friendsRes.data) ? friendsRes.data : []);
        
        // Map API response to Friend interface
        const mappedFriends: Friend[] = friendsArray.map((friendship: any) => {
          const friendProfile = friendship.friendProfile || friendship.profile || friendship;
          // Use friendProfile.id as the id (profile ID), not friendshipId
          // The API expects recipientId to be the profile ID, not friendship ID
          return {
            id: friendProfile.id || friendProfile.userId, // Profile ID (not friendshipId)
            userId: friendProfile.userId || friendProfile.id,
            customerProfileId: friendProfile.id || friendProfile.userId,
            firstName: friendProfile.firstName || friendProfile.displayName?.split(' ')[0] || '',
            lastName: friendProfile.lastName || friendProfile.displayName?.split(' ').slice(1).join(' ') || '',
            fullName: friendProfile.displayName || `${friendProfile.firstName || ''} ${friendProfile.lastName || ''}`.trim() || 'Unknown',
            email: friendProfile.email || '',
            avatar: friendProfile.profilePic || friendProfile.profilePicture,
            profilePicture: friendProfile.profilePic || friendProfile.profilePicture,
            skillLevel: friendProfile.skillLevel,
            status: 'accepted',
            createdAt: friendship.acceptedAt || friendship.friendshipCreatedAt || new Date().toISOString(),
            updatedAt: friendship.acceptedAt
          };
        });
        
        if (mappedFriends.length > 0) {
          console.log(`[Friends] Loaded ${mappedFriends.length} friends from API`);
        }
        this.setState({ friends: mappedFriends });
      } else {
        this.setState({ friends: [] });
      }

      if (requestsRes.success && requestsRes.data) {
        const received = requestsRes.data.received || [];
        const sent = requestsRes.data.sent || [];
        
        // Map received requests to Friend interface
        const mappedReceived: Friend[] = received.map((request: any) => {
          const profile = request.profile || {};
          return {
            id: request.id,
            userId: profile.userId || request.requesterId,
            customerProfileId: profile.id || request.requesterId,
            firstName: profile.firstName || profile.displayName?.split(' ')[0] || '',
            lastName: profile.lastName || profile.displayName?.split(' ').slice(1).join(' ') || '',
            fullName: profile.displayName || `${profile.firstName || ''} ${profile.lastName || ''}`.trim() || 'Unknown',
            email: profile.email || '',
            avatar: profile.profilePic || profile.profilePicture,
            profilePicture: profile.profilePic || profile.profilePicture,
            skillLevel: profile.skillLevel,
            status: request.status || 'pending',
            createdAt: request.createdAt || new Date().toISOString(),
            updatedAt: request.updatedAt,
            requestMessage: request.requestMessage,
            acceptComment: request.acceptComment,
            rejectComment: request.rejectionComment || request.rejectComment
          };
        });
        
        // Map sent requests to Friend interface
        const mappedSent: Friend[] = sent.map((request: any) => {
          const profile = request.profile || {};
          return {
            id: request.id,
            userId: profile.userId || request.recipientId,
            customerProfileId: profile.id || request.recipientId,
            firstName: profile.firstName || profile.displayName?.split(' ')[0] || '',
            lastName: profile.lastName || profile.displayName?.split(' ').slice(1).join(' ') || '',
            fullName: profile.displayName || `${profile.firstName || ''} ${profile.lastName || ''}`.trim() || 'Unknown',
            email: profile.email || '',
            avatar: profile.profilePic || profile.profilePicture,
            profilePicture: profile.profilePic || profile.profilePicture,
            skillLevel: profile.skillLevel,
            status: request.status || 'sent',
            createdAt: request.createdAt || new Date().toISOString(),
            updatedAt: request.updatedAt,
            requestMessage: request.requestMessage,
            acceptComment: request.acceptComment,
            rejectComment: request.rejectionComment || request.rejectComment
          };
        });
        
        if (mappedReceived.length > 0 || mappedSent.length > 0) {
          console.log(`[Friends] Loaded ${mappedReceived.length} received and ${mappedSent.length} sent friend requests`);
        }
        this.setState({
          friendRequests: mappedReceived,
          sentRequests: mappedSent
        });
      } else {
        this.setState({
          friendRequests: [],
          sentRequests: []
        });
      }

      // API returns data as array directly, not { groups: [], pagination: {} }
      if (groupsRes.success && groupsRes.data && Array.isArray(groupsRes.data) && groupsRes.data.length > 0) {
        // Extract user roles from groups - need to get from group details or members
        // For now, we'll load group details to get member roles
        const userRoles: Record<string, 'admin' | 'moderator' | 'member'> = {};
        const groupsWithDetails = await Promise.all(
          groupsRes.data.map(async (group: any) => {
            try {
              const detailsRes = await apiService.getGroupDetails(group.id);
              if (detailsRes.success && detailsRes.data && detailsRes.data.members) {
                // Find current user's role in members
                const currentUserMember = detailsRes.data.members.find(
                  (m: any) => (m.customerId === this.state.currentUserProfileId) || (m.memberId === this.state.currentUserProfileId)
                );
                if (currentUserMember) {
                  // Map super_admin to admin for GroupChat component
                  const role = currentUserMember.role || 'member';
                  userRoles[group.id] = role === 'super_admin' ? 'admin' : (role as 'admin' | 'moderator' | 'member');
                }
                // Store members for this group
                this.setState(prevState => ({
                  groupMembers: {
                    ...prevState.groupMembers,
                    [group.id]: detailsRes.data.members
                  }
                }));
              }
            } catch (error) {
              console.error(`[Friends] Failed to load details for group ${group.id}:`, error);
            }
            
            // Map API response to FriendGroup interface
            return {
              id: group.id,
              name: group.name,
              description: group.description || undefined,
              memberCount: group.memberCount || 0,
              avatarUrl: group.avatarUrl || undefined,
              profilePicUrl: group.avatarUrl || group.profilePicUrl || undefined, // Map avatarUrl to profilePicUrl
              createdBy: group.createdBy,
              creator: group.creator,
              createdAt: group.createdAt,
              updatedAt: group.updatedAt
            } as FriendGroup;
          })
        );
        
        this.setState({ 
          groups: groupsWithDetails,
          userRoleInGroup: userRoles
        });
      } else {
        this.setState({ groups: [] });
      }

      // Load group invitations
      if (invitationsRes.success && invitationsRes.data) {
        this.setState({ groupInvitations: invitationsRes.data });
      }
    } catch (error) {
      console.error('[Friends] Failed to load data:', error);
      this.setState({
        players: [],
        friends: [],
        friendRequests: [],
        sentRequests: [],
        groups: [],
        loading: false
      });
    } finally {
      // Only set loading to false if not already set in catch block
      if (this.state.loading) {
        this.setState({ loading: false });
      }
    }
  };

  handleOpenAddFriendModal = (player: Player) => {
    this.setState({
      showAddFriendModal: true,
      selectedPlayer: player,
      friendRequestComment: ''
    });
  };

  handleCloseAddFriendModal = () => {
    this.setState({
      showAddFriendModal: false,
      selectedPlayer: null,
      friendRequestComment: ''
    });
  };

  handleSendFriendRequest = async (recipientId: string, message?: string) => {
    try {
      this.setState({ loading: true });
      const response = await apiService.sendFriendRequest(recipientId, message);
      if (response.success) {
        // Show success message (you can add a toast notification here)
        console.log('[Friends] Friend request sent successfully:', response.data);
        this.handleCloseAddFriendModal();
        // Reload friends data to update the UI
        await this.loadFriendsData();
        // Optionally show a success notification
        // You can add a toast/notification system here
      } else {
        // Handle API error response
        console.error('[Friends] Failed to send request:', response.message);
        alert(response.message || 'Failed to send connection request. Please try again.');
      }
    } catch (error: any) {
      console.error('[Friends] Failed to send request:', error);
      const errorMessage = error?.response?.data?.message || error?.message || 'Failed to send connection request. Please try again.';
      alert(errorMessage);
    } finally {
      this.setState({ loading: false });
    }
  };

  handleConfirmAddFriend = () => {
    if (this.state.selectedPlayer) {
      this.handleSendFriendRequest(this.state.selectedPlayer.userId, this.state.friendRequestComment);
    }
  };

  handleOpenAcceptModal = (request: Friend) => {
    this.setState({
      showAcceptModal: true,
      selectedRequest: request,
      acceptComment: ''
    });
  };

  handleCloseAcceptModal = () => {
    this.setState({
      showAcceptModal: false,
      selectedRequest: null,
      acceptComment: ''
    });
  };

  handleOpenRejectModal = (request: Friend) => {
    this.setState({
      showRejectModal: true,
      selectedRequest: request,
      rejectComment: ''
    });
  };

  handleCloseRejectModal = () => {
    this.setState({
      showRejectModal: false,
      selectedRequest: null,
      rejectComment: ''
    });
  };

  handleConfirmAccept = async () => {
    if (!this.state.selectedRequest) return;
    
    try {
      this.setState({ loading: true });
      const response = await apiService.acceptFriendRequest(
        this.state.selectedRequest.id,
        this.state.acceptComment || undefined
      );
      if (response.success) {
        this.handleCloseAcceptModal();
        await this.loadFriendsData();
      } else {
        alert(response.message || 'Failed to accept connection request. Please try again.');
      }
    } catch (error: any) {
      console.error('[Friends] Failed to accept request:', error);
      const errorMessage = error?.response?.data?.message || error?.message || 'Failed to accept connection request. Please try again.';
      alert(errorMessage);
    } finally {
      this.setState({ loading: false });
    }
  };

  handleConfirmReject = async () => {
    if (!this.state.selectedRequest) return;
    
    try {
      this.setState({ loading: true });
      const response = await apiService.rejectFriendRequest(
        this.state.selectedRequest.id,
        this.state.rejectComment || undefined
      );
      if (response.success) {
        this.handleCloseRejectModal();
        await this.loadFriendsData();
      } else {
        alert(response.message || 'Failed to decline connection request. Please try again.');
      }
    } catch (error: any) {
      console.error('[Friends] Failed to reject request:', error);
      const errorMessage = error?.response?.data?.message || error?.message || 'Failed to decline connection request. Please try again.';
      alert(errorMessage);
    } finally {
      this.setState({ loading: false });
    }
  };

  handleCancelRequest = async (requestId: string) => {
    try {
      const response = await apiService.cancelFriendRequest(requestId);
      if (response.success) {
        await this.loadFriendsData();
      }
    } catch (error) {
      console.error('[Friends] Failed to cancel request:', error);
      // For preview: Remove from sent requests
      this.setState(prevState => ({
        sentRequests: prevState.sentRequests.filter(r => r.id !== requestId)
      }));
    }
  };

  handleRemoveFriend = async (friendId: string) => {
    if (!window.confirm('Remove this connection?')) {
      return;
    }
    
    try {
      const response = await apiService.removeFriend(friendId);
      if (response.success) {
        await this.loadFriendsData();
      }
    } catch (error) {
      console.error('[Friends] Failed to remove friend:', error);
      // For preview: Remove from friends list
      this.setState(prevState => ({
        friends: prevState.friends.filter(f => f.id !== friendId)
      }));
    }
  };

  handleOpenChat = (friend: Friend) => {
    console.log('[Friends] Opening chat with friend:', friend);
    console.log('[Friends] Friend ID:', friend.id);
    console.log('[Friends] Friend userId:', friend.userId);
    console.log('[Friends] Current user profile ID:', this.state.currentUserProfileId);
    
    // If switching to a different friend, leave the previous chat room first so the old chat closes and we open fresh with new data
    const previousFriend = this.state.selectedFriendForChat;
    if (previousFriend && previousFriend.id !== friend.id && previousFriend.userId && friendsChatWebSocketService.isConnected()) {
      friendsChatWebSocketService.leaveChatRoom(previousFriend.userId);
    }
    
    // Verify socket is connected and event handlers are registered
    console.log('[Friends] Socket connected?', friendsChatWebSocketService.isConnected());
    console.log('[Friends] Socket event handlers registered?', friendsChatWebSocketService.isConnected());
    
    this.setState({
      showChat: true,
      selectedFriendForChat: friend
    });
    
    // Join chat room when opening chat
    // This ensures we receive messages in real-time via chat rooms
    // Messages are also sent to user rooms, but chat rooms provide better routing
    const joinChatRoom = () => {
      if (friend.userId) {
        if (friendsChatWebSocketService.isConnected()) {
          console.log('[Friends] ✅ Socket connected, joining chat room for friend:', friend.userId);
          friendsChatWebSocketService.joinChatRoom(friend.userId);
        } else {
          console.log('[Friends] ⚠️ Socket not connected yet, retrying in 500ms');
          // Retry after a short delay if socket is not connected
          setTimeout(() => {
            if (friendsChatWebSocketService.isConnected()) {
              console.log('[Friends] ✅ Socket now connected, joining chat room');
              friendsChatWebSocketService.joinChatRoom(friend.userId);
            } else {
              console.warn('[Friends] ❌ Socket still not connected, messages may not update in real-time');
            }
          }, 500);
        }
      } else {
        console.warn('[Friends] ⚠️ Friend userId not available, cannot join chat room');
      }
    };
    
    // Try immediately, and also set up a listener for when socket connects
    joinChatRoom();
    
    // Also listen for connection event to join room
    const onConnect = () => {
      if (this.state.selectedFriendForChat?.userId === friend.userId) {
        console.log('[Friends] Socket connected, joining chat room for friend:', friend.userId);
        friendsChatWebSocketService.joinChatRoom(friend.userId);
        friendsChatWebSocketService.off('connect', onConnect);
      }
    };
    friendsChatWebSocketService.on('connect', onConnect);
    
    // IMPORTANT: Reload messages after opening chat to ensure we have the latest messages
    // This ensures any messages received via socket before opening are included
    // Use markAsRead=true to automatically mark all unread messages as read (per API documentation)
    // Multiple reloads to handle timing issues:
    // 1. First reload after Chat component mounts (500ms) - allows component to initialize
    // 2. Second reload after more time (1500ms) - ensures API has latest messages
    // 3. Third reload after even more time (2500ms) - final check to ensure all messages are loaded
    setTimeout(() => {
      if (this.handleLoadMessages && friend.id && this.state.selectedFriendForChat?.id === friend.id) {
        console.log('[Friends] 🔄 First reload: Reloading messages after opening chat with markAsRead=true (500ms delay)');
        this.handleLoadMessages(friend.id, true);
      }
    }, 500);
    
    // Second reload to ensure we have the absolute latest messages
    setTimeout(() => {
      if (this.handleLoadMessages && friend.id && this.state.selectedFriendForChat?.id === friend.id) {
        console.log('[Friends] 🔄 Second reload: Reloading messages again to ensure latest (1500ms delay)');
        this.handleLoadMessages(friend.id, true);
      }
    }, 1500);
    
    // Third reload as final check to ensure all messages are loaded
    setTimeout(() => {
      if (this.handleLoadMessages && friend.id && this.state.selectedFriendForChat?.id === friend.id) {
        console.log('[Friends] 🔄 Third reload: Final reload to ensure all messages are loaded (2500ms delay)');
        this.handleLoadMessages(friend.id, true);
      }
    }, 2500);
  };

  handleOpenGroupPosts = async (group: FriendGroup) => {
    // Load group members if not already loaded
    if (!this.state.groupMembers[group.id]) {
      await this.loadGroupMembers(group.id);
    }

    this.setState({
      showGroupPosts: true,
      selectedGroupForChat: group,
      showChat: false, // Close individual chat if open
      selectedFriendForChat: null
    });
  };

  handleCloseGroupPosts = () => {
    this.setState({
      showGroupPosts: false,
      selectedGroupForChat: null
    });
  };

  handleOpenGroupChat = async (group: FriendGroup) => {
    // Load group members if not already loaded
    if (!this.state.groupMembers[group.id]) {
      await this.loadGroupMembers(group.id);
    }

    // Ensure socket is connected
    if (!groupChatWebSocketService.isConnected()) {
      groupChatWebSocketService.connect();
      // Wait for connection to be established
      await new Promise<void>((resolve) => {
        const checkConnection = () => {
          if (groupChatWebSocketService.isConnected()) {
            resolve();
          } else {
            setTimeout(checkConnection, 100);
          }
        };
        checkConnection();
      });
    }

    // Join group room for live messages
    groupChatWebSocketService.joinGroupRoom(group.id);
    console.log('[Friends] Joined group room:', group.id);

    // Clear unread count for this group when opening chat
    this.setState(prevState => ({
      showGroupChat: true,
      selectedGroupForChat: group,
      showChat: false, // Close individual chat if open
      showGroupPosts: false, // Close group posts if open
      selectedFriendForChat: null,
      groupUnreadCounts: {
        ...prevState.groupUnreadCounts,
        [group.id]: 0 // Reset unread count
      }
    }));
  };

  handleCloseGroupChat = () => {
    const { selectedGroupForChat } = this.state;
    
    // Leave group room when closing chat
    if (selectedGroupForChat?.id && groupChatWebSocketService.isConnected()) {
      groupChatWebSocketService.leaveGroupRoom(selectedGroupForChat.id);
      console.log('[Friends] Left group room:', selectedGroupForChat.id);
    }
    
    this.setState({
      showGroupChat: false,
      selectedGroupForChat: null
    });
  };

  handleLoadGroupMessages = async (groupId: string): Promise<any[]> => {
    try {
      console.log('[Friends] Loading group messages for group:', groupId);
      const response = await apiService.getGroupMessages(groupId, 1, 50);
      
      if (response.success && response.data) {
        const messages = response.data.messages || [];
        console.log('[Friends] Loaded group messages:', messages.length);
        return messages;
      }
      return [];
    } catch (error) {
      console.error('[Friends] Failed to load group messages:', error);
      return [];
    }
  };

  handleSendGroupMessage = async (
    groupId: string, 
    messageText: string, 
    replyToMessageId?: string, 
    attachments?: File[],
    location?: {
      latitude: number;
      longitude: number;
      address?: string;
      placeName?: string;
    }
  ) => {
    const { selectedGroupForChat } = this.state;
    if (!selectedGroupForChat) return;

    try {
      console.log('[Friends] 📤 Sending group message to:', groupId);
      
      // Send message via API
      // Note: GroupChat component handles optimistic updates internally
      const response = await apiService.sendGroupMessage(
        groupId,
        messageText,
        replyToMessageId,
        attachments,
        location
      );
      
      console.log('[Friends] Group message send API response:', response);
      
      // If API returns the message and socket is not available, add it to the chat
      // The GroupChat component already handles optimistic updates, but we add the real message
      // if socket events are not available
      if (response.success && response.data && this.groupChatRef.current?.addMessage) {
        const sentMessage = response.data;
        const groupMessage: any = {
          id: sentMessage.id,
          senderId: sentMessage.senderId,
          groupId: sentMessage.groupId || groupId,
          messageText: sentMessage.messageText || messageText,
          replyToMessage: sentMessage.replyToMessage,
          attachments: sentMessage.attachments || [],
          createdAt: sentMessage.createdAt || new Date().toISOString(),
          updatedAt: sentMessage.updatedAt || new Date().toISOString(),
          sender: sentMessage.sender
        };
        
        // addMessage checks for duplicates, so this is safe
        this.groupChatRef.current.addMessage(groupMessage);
      }
    } catch (error) {
      console.error('[Friends] Failed to send group message:', error);
      throw error;
    }
  };

  handleCloseChat = () => {
    const { selectedFriendForChat } = this.state;
    
    // Leave chat room when closing chat
    if (selectedFriendForChat?.userId && friendsChatWebSocketService.isConnected()) {
      friendsChatWebSocketService.leaveChatRoom(selectedFriendForChat.userId);
    }
    
    this.setState({
      showChat: false,
      selectedFriendForChat: null,
      isFriendTyping: false
    });
    
    // Stop typing if user was typing
    if (this.state.isTyping && selectedFriendForChat?.userId) {
      friendsChatWebSocketService.stopTyping(selectedFriendForChat.userId);
    }
    
    // Refresh unread counts when chat is closed
    this.loadUnreadCounts();
  };

  handleSendMessage = async (
    recipientId: string, 
    messageText: string, 
    replyToMessageId?: string, 
    attachments?: File[],
    location?: {
      latitude: number;
      longitude: number;
      address?: string;
      placeName?: string;
    }
  ) => {
    const { selectedFriendForChat, currentUserProfileId } = this.state;
    if (!selectedFriendForChat) return;

    try {
      console.log('[Friends] 📤 Sending message to:', recipientId);
      console.log('[Friends] Socket connected?', friendsChatWebSocketService.isConnected());
      
      // Store message details to match with socket event
      const messageToSend = {
        recipientId,
        messageText,
        replyToMessageId,
        timestamp: Date.now()
      };
      
      // Send message via API
      const response = await apiService.sendMessage(
        recipientId, // This should be the friend's profile ID
        messageText,
        replyToMessageId,
        attachments,
        location
      );
      
      console.log('[Friends] Message send API response:', response);
      
      // ✅ CORRECT: Don't add message from API response
      // Wait for socket event 'message:sent' to update the chat
      // The socket event will have the complete message with attachments
      // This prevents duplicate messages
      if (friendsChatWebSocketService.isConnected()) {
        console.log('[Friends] ✅ Socket connected, waiting for message:sent event to update chat');
        console.log('[Friends] The handleMessageSent handler will update the chat when socket event arrives');
      } else {
        console.log('[Friends] ⚠️ Socket not connected, but will wait for reconnection');
        // Only add from API as fallback if socket is not connected AND we don't get socket event within 3 seconds
        if (response.success && response.data && selectedFriendForChat) {
          setTimeout(() => {
            // Check if message was already added via socket
            if (this.chatRef.current) {
              const messages = this.chatRef.current.state?.messages || [];
              const messageExists = messages.some((m: any) => m.id === response.data.id);
              
              if (!messageExists && !friendsChatWebSocketService.isConnected()) {
                console.log('[Friends] ⚠️ Socket still not connected after 3s, adding message from API response as fallback');
                const sentMessage = response.data;
                
                if (this.chatRef.current && this.chatRef.current.addMessage) {
                  const chatMessage: any = {
                    id: sentMessage.id,
                    senderId: sentMessage.senderId || currentUserProfileId,
                    recipientId: sentMessage.recipientId || recipientId,
                    messageText: sentMessage.messageText || messageText,
                    replyToMessage: sentMessage.replyToMessage,
                    isRead: sentMessage.isRead || false,
                    readAt: sentMessage.readAt,
                    attachments: sentMessage.attachments || [],
                    createdAt: sentMessage.createdAt || new Date().toISOString(),
                    updatedAt: sentMessage.updatedAt || new Date().toISOString(),
                    sender: sentMessage.sender,
                    recipient: sentMessage.recipient
                  };
                  
                  this.chatRef.current.addMessage(chatMessage);
                }
              }
            }
          }, 3000);
        }
      }
      
      // Set up a fallback reload in case socket events don't fire within reasonable time
      // This ensures messages appear even if socket fails or is delayed
      if (this.handleLoadMessages && selectedFriendForChat) {
        console.log('[Friends] Setting up fallback reload after 1 second (in case socket event is delayed)');
        setTimeout(async () => {
          // Check if message was already added by socket event
          // If not, reload messages as fallback
          if (this.chatRef.current) {
            console.log('[Friends] Fallback: Reloading messages to ensure both users see update');
            await this.handleLoadMessages(selectedFriendForChat.id);
          }
        }, 1000); // Increased to 1 second to give socket events time to arrive
      }
      
    } catch (error) {
      console.error('[Friends] Failed to send message:', error);
      throw error; // Let Chat component handle the error
    }
  };

  handleMarkAsRead = async (friendId: string, messageIds?: string[]) => {
    try {
      await apiService.markMessagesAsRead(friendId, messageIds);
      // Update unread count after marking as read
      this.setState(prevState => ({
        unreadCounts: {
          ...prevState.unreadCounts,
          [friendId]: 0
        }
      }));
    } catch (error) {
      console.error('[Friends] Failed to mark messages as read:', error);
    }
  };

  loadUnreadCounts = async () => {
    try {
      const response = await apiService.getUnreadCount();
      if (response.success && response.data) {
        const unreadCounts: Record<string, number> = {};
        
        // Map conversations to friend IDs
        if (response.data.conversations && Array.isArray(response.data.conversations)) {
          response.data.conversations.forEach((conv: any) => {
            if (conv.friendId && conv.unreadCount > 0) {
              unreadCounts[conv.friendId] = conv.unreadCount;
            }
          });
        }
        
        this.setState({ unreadCounts });
      }
    } catch (error) {
      console.error('[Friends] Failed to load unread counts:', error);
    }
  };

  handleLoadMessages = async (friendId: string, markAsRead: boolean = true) => {
    try {
      // friendId should be the friend's profile ID
      // markAsRead=true when opening chat box (per API documentation)
      // This automatically marks all unread messages as read and notifies sender via Socket.IO
      const response = await apiService.getConversation(friendId, 1, 50, undefined, markAsRead);
      if (response.success && response.data) {
        // Map API messages to Chat component format
        const messages = (response.data.messages || []).map((msg: any) => ({
          id: msg.id,
          senderId: msg.senderId, // Profile ID
          recipientId: msg.recipientId, // Profile ID
          messageText: msg.messageText || '',
          replyToMessage: msg.replyToMessage ? {
            id: msg.replyToMessage.id,
            messageText: msg.replyToMessage.messageText,
            senderId: msg.replyToMessage.senderId,
            senderName: msg.replyToMessage.senderName,
            senderProfilePic: msg.replyToMessage.senderProfilePic,
            createdAt: msg.replyToMessage.createdAt
          } : undefined,
          isRead: msg.isRead || false,
          readAt: msg.readAt || undefined,
          attachments: msg.attachments || [],
          createdAt: msg.createdAt,
          updatedAt: msg.updatedAt,
          sender: msg.sender,
          recipient: msg.recipient
        }));
        
        // If markAsRead was false, manually mark unread messages as read
        // (This is a fallback, but API should handle it when markAsRead=true)
        if (!markAsRead) {
          const unreadMessageIds = messages
            .filter(m => !m.isRead && m.senderId !== (this.state.currentUserProfileId || ''))
            .map(m => m.id);
          if (unreadMessageIds.length > 0 && this.handleMarkAsRead) {
            await this.handleMarkAsRead(friendId, unreadMessageIds);
          }
        }
        
        return messages;
      }
      return [];
    } catch (error) {
      console.error('[Friends] Failed to load messages:', error);
      return [];
    }
  };

  handleCreateGroup = async () => {
    if (!this.state.newGroupName.trim()) {
      alert('Please enter a group name');
      return;
    }

    try {
      const response = await apiService.createGroup(
        this.state.newGroupName,
        this.state.newGroupDescription || undefined,
        this.state.newGroupProfilePic || undefined,
        this.state.selectedFriendsForGroup.length > 0 ? this.state.selectedFriendsForGroup : undefined
      );

      if (response.success) {
        // Clean up profile picture preview URL if exists
        if (this.state.newGroupProfilePicPreview) {
          URL.revokeObjectURL(this.state.newGroupProfilePicPreview);
        }
        
        this.setState({
          showCreateGroupModal: false,
          newGroupName: '',
          newGroupDescription: '',
          newGroupColor: '#3b82f6',
          newGroupProfilePic: null,
          newGroupProfilePicPreview: null,
          selectedFriendsForGroup: []
        });
        await this.loadFriendsData();
        await this.loadGroupInvitations();
      } else {
        alert(response.message || 'Failed to create group. Please try again.');
      }
    } catch (error: any) {
      console.error('[Friends] Failed to create group:', error);
      alert(error?.response?.data?.message || 'Failed to create group. Please try again.');
    }
  };

  handleToggleFriendSelection = (friendId: string) => {
    this.setState(prevState => {
      const isSelected = prevState.selectedFriendsForGroup.includes(friendId);
      return {
        selectedFriendsForGroup: isSelected
          ? prevState.selectedFriendsForGroup.filter(id => id !== friendId)
          : [...prevState.selectedFriendsForGroup, friendId]
      };
    });
  };

  loadGroupInvitations = async () => {
    try {
      const response = await apiService.getPendingInvitations();
      if (response.success && response.data) {
        this.setState({ groupInvitations: response.data });
      }
    } catch (error) {
      console.error('[Friends] Failed to load group invitations:', error);
    }
  };

  handleAcceptGroupInvitation = async (invitationId: string) => {
    try {
      const response = await apiService.respondToInvitation(invitationId, 'accept');
      if (response.success) {
        await this.loadFriendsData();
        await this.loadGroupInvitations();
        alert('Group invitation accepted successfully!');
      } else {
        alert(response.message || 'Failed to accept group invitation. Please try again.');
      }
    } catch (error: any) {
      console.error('[Friends] Failed to accept group invitation:', error);
      alert(error?.response?.data?.message || 'Failed to accept group invitation. Please try again.');
    }
  };

  handleRejectGroupInvitation = async (invitationId: string) => {
    try {
      const response = await apiService.respondToInvitation(invitationId, 'reject');
      if (response.success) {
        await this.loadGroupInvitations();
      } else {
        alert(response.message || 'Failed to reject group invitation. Please try again.');
      }
    } catch (error: any) {
      console.error('[Friends] Failed to reject group invitation:', error);
      alert(error?.response?.data?.message || 'Failed to reject group invitation. Please try again.');
    }
  };

  loadGroupMembers = async (groupId: string) => {
    try {
      const response = await apiService.getGroupDetails(groupId);
      if (response.success && response.data && response.data.members) {
        this.setState(prevState => ({
          groupMembers: {
            ...prevState.groupMembers,
            [groupId]: response.data.members
          }
        }));
        // Update user role
        const currentUserMember = response.data.members.find(
          (m: any) => m.memberId === this.state.currentUserProfileId
        );
        if (currentUserMember) {
          this.setState(prevState => ({
            userRoleInGroup: {
              ...prevState.userRoleInGroup,
              [groupId]: currentUserMember.role || 'member'
            }
          }));
        }
      }
    } catch (error) {
      console.error('[Friends] Failed to load group members:', error);
    }
  };

  handleRemoveGroupMember = async (groupId: string, memberId: string) => {
    if (!window.confirm('Are you sure you want to remove this member from the group?')) {
      return;
    }

    try {
      const response = await apiService.removeGroupMember(groupId, memberId);
      if (response.success) {
        await this.loadGroupMembers(groupId);
        await this.loadFriendsData();
      }
    } catch (error) {
      console.error('[Friends] Failed to remove group member:', error);
      alert('Failed to remove member. Please try again.');
    }
  };

  handleInviteToGroup = async (groupId: string, friendIds: string[], invitationMessage?: string) => {
    try {
      const response = await apiService.inviteToGroup(groupId, friendIds, invitationMessage);
      if (response.success) {
        await this.loadFriendsData();
        alert(`Successfully sent ${response.data?.length || friendIds.length} invitation(s)!`);
      } else {
        alert(response.message || 'Failed to send invitations. Please try again.');
      }
    } catch (error: any) {
      console.error('[Friends] Failed to invite to group:', error);
      alert(error?.response?.data?.message || 'Failed to send invitations. Please try again.');
    }
  };

  handleOpenAddMembersModal = async () => {
    const group = this.state.selectedGroupForChat;
    if (!group) return;
    await this.loadGroupMembers(group.id);
    this.setState({
      showAddMembersModal: true,
      selectedGroupForAddMembers: group,
      selectedFriendIdsToAdd: []
    });
  };

  handleCloseAddMembersModal = () => {
    this.setState({
      showAddMembersModal: false,
      selectedGroupForAddMembers: null,
      selectedFriendIdsToAdd: []
    });
  };

  /** Normalize ID for comparison (API may return different shapes; compare case-insensitive). */
  private normalizeId = (value: any): string => {
    if (value == null || value === '') return '';
    const s = String(value).trim().toLowerCase();
    return s;
  };

  /** Friends who are not already in the current group (for Add Members modal). Uses IDs from GET /customers/groups/:id. */
  getFriendsNotInGroup = (): Friend[] => {
    const group = this.state.selectedGroupForAddMembers;
    if (!group) return [];
    const members = this.state.groupMembers[group.id] || [];
    const memberProfileIds = new Set<string>();
    members.forEach((m: any) => {
      const ids = [
        m.id,
        m.profileId,
        m.memberId,
        m.customerId,
        m.customerProfileId,
        m.customer?.id,
        m.profile?.id,
        m.member?.id,
        m.userId
      ];
      ids.forEach((id) => {
        const n = this.normalizeId(id);
        if (n) memberProfileIds.add(n);
      });
    });
    return this.getFilteredFriends().filter((f) => {
      const a = this.normalizeId(f.id);
      const b = this.normalizeId(f.customerProfileId);
      if (!a && !b) return true;
      return !memberProfileIds.has(a) && !memberProfileIds.has(b);
    });
  };

  handleToggleAddMemberFriend = (friendId: string) => {
    this.setState(prevState => {
      const isSelected = prevState.selectedFriendIdsToAdd.includes(friendId);
      return {
        selectedFriendIdsToAdd: isSelected
          ? prevState.selectedFriendIdsToAdd.filter(id => id !== friendId)
          : [...prevState.selectedFriendIdsToAdd, friendId]
      };
    });
  };

  handleAddGroupMembers = async () => {
    const { selectedGroupForAddMembers, selectedFriendIdsToAdd } = this.state;
    if (!selectedGroupForAddMembers || selectedFriendIdsToAdd.length === 0) return;
    try {
      const response = await apiService.addGroupMembers(selectedGroupForAddMembers.id, selectedFriendIdsToAdd);
      if (response.success) {
        await this.loadGroupMembers(selectedGroupForAddMembers.id);
        const added = response.data?.added ?? 0;
        alert(added > 0 ? `Added ${added} member(s) to the group.` : (response.message || 'No new members added (already in group or not connected).'));
        this.handleCloseAddMembersModal();
      } else {
        alert(response.message || 'Failed to add members.');
      }
    } catch (error: any) {
      console.error('[Friends] Failed to add group members:', error);
      alert(error?.response?.data?.message || error?.message || 'Failed to add members. Please try again.');
    }
  };

  handleDeleteGroup = async (groupId: string) => {
    if (!window.confirm('Are you sure you want to delete this group? This will permanently delete the group, all members, posts, and comments. This action cannot be undone.')) {
      return;
    }

    try {
      const response = await apiService.deleteGroup(groupId);
      if (response.success) {
        await this.loadFriendsData();
        alert('Group deleted successfully');
      } else {
        alert(response.message || 'Failed to delete group. Please try again.');
      }
    } catch (error: any) {
      console.error('[Friends] Failed to delete group:', error);
      alert(error?.response?.data?.message || 'Failed to delete group. Please try again.');
    }
  };

  // Note: Edit group functionality not available in API - only delete is supported

  // Group Chat Socket Event Handlers
  handleGroupCreated = (data: { groupId: string; name: string }) => {
    console.log('[Friends] Group created via socket:', data);
    // Reload groups to show the new group
    this.loadFriendsData();
  };

  // Note: handleGroupMessageNew is not needed - GroupChat component handles group:message:new directly
  // This prevents duplicate message handling. Only notifications are handled here.

  handleGroupMessageNotification = (data: {
    groupId: string;
    groupName: string;
    messageId: string;
    senderName: string;
    senderProfilePic?: string;
    messagePreview: string;
    hasAttachments: boolean;
    createdAt: string;
  }) => {
    console.log('[Friends] Group message notification via socket:', data);
    
    // Only increment if chat is closed or for a different group
    if (!this.state.showGroupChat || this.state.selectedGroupForChat?.id !== data.groupId) {
      // Increment unread count for this group
      this.setState(prevState => ({
        groupUnreadCounts: {
          ...prevState.groupUnreadCounts,
          [data.groupId]: (prevState.groupUnreadCounts[data.groupId] || 0) + 1
        }
      }));

      // Show browser notification if permission is granted
      if ('Notification' in window && Notification.permission === 'granted') {
        try {
          new Notification(data.groupName, {
            body: `${data.senderName}: ${data.messagePreview}`,
            icon: data.senderProfilePic || '/default-avatar.png',
            badge: '/notification-badge.png',
            tag: `group-${data.groupId}`,
            data: {
              groupId: data.groupId,
              messageId: data.messageId
            }
          });
        } catch (error) {
          console.error('[Friends] Failed to show browser notification:', error);
        }
      }
    }
  };

  handleGroupCommentNew = (data: { groupId: string; messageId: string; comment: any }) => {
    console.log('[Friends] New group comment via socket:', data);
    
    // Only handle if this is the currently open group chat
    if (this.state.showGroupChat && this.state.selectedGroupForChat?.id === data.groupId) {
      // Add comment to the group chat component
      if (this.groupChatRef.current?.addComment) {
        this.groupChatRef.current.addComment(data.messageId, data.comment);
      }
    }
  };

  handleGroupCommentSentimentUpdated = (data: {
    groupId: string;
    messageId: string;
    commentId: string;
    sentiment: 'positive' | 'negative';
    customerId: string;
  }) => {
    console.log('[Friends] Group comment sentiment updated via socket:', data);
    
    // Only handle if this is the currently open group chat
    if (this.state.showGroupChat && this.state.selectedGroupForChat?.id === data.groupId) {
      // Update comment sentiment in the group chat component
      if (this.groupChatRef.current?.updateCommentSentiment) {
        this.groupChatRef.current.updateCommentSentiment(
          data.messageId,
          data.commentId,
          data.sentiment,
          data.customerId
        );
      }
    }
  };

  handleGroupCommentSentimentRemoved = (data: {
    groupId: string;
    messageId: string;
    commentId: string;
    customerId: string;
  }) => {
    console.log('[Friends] Group comment sentiment removed via socket:', data);
    
    // Only handle if this is the currently open group chat
    if (this.state.showGroupChat && this.state.selectedGroupForChat?.id === data.groupId) {
      // Remove comment sentiment in the group chat component
      if (this.groupChatRef.current?.updateCommentSentiment) {
        this.groupChatRef.current.updateCommentSentiment(
          data.messageId,
          data.commentId,
          null,
          data.customerId
        );
      }
    }
  };

  getFilteredFriends = () => {
    const { friends, searchTerm } = this.state;
    if (!searchTerm) return friends;
    
    const term = searchTerm.toLowerCase();
    return friends.filter(friend => 
      friend.fullName?.toLowerCase().includes(term) ||
      friend.firstName?.toLowerCase().includes(term) ||
      friend.lastName?.toLowerCase().includes(term) ||
      friend.email?.toLowerCase().includes(term)
    );
  };

  getFilteredGroups = () => {
    const { groups, searchTerm } = this.state;
    if (!searchTerm) return groups;
    
    const term = searchTerm.toLowerCase();
    return groups.filter(group => 
      group.name.toLowerCase().includes(term) ||
      group.description?.toLowerCase().includes(term)
    );
  };

  getFilteredPlayers = () => {
    const { players, searchTerm } = this.state;
    // Ensure players array exists
    if (!players || !Array.isArray(players)) {
      return [];
    }
    // API already returns only public players, so no need to filter by profileMode
    // Only validate that player exists and has at least one identifier
    const validPlayers = players.filter(player => 
      player && 
      (player.id || player.userId) // Only require at least one identifier
    );
    
    if (!searchTerm || !searchTerm.trim()) {
      console.log(`[Friends] getFilteredPlayers: Total players in state: ${players.length}, Valid players: ${validPlayers.length} (no search term)`);
      if (validPlayers.length < players.length) {
        console.warn(`[Friends] getFilteredPlayers: Filtered out ${players.length - validPlayers.length} players. Invalid players:`, 
          players.filter(p => !p || !p.id && !p.userId).map(p => ({ id: p?.id, userId: p?.userId, displayName: p?.displayName }))
        );
      }
      return validPlayers;
    }
    
    const term = searchTerm.toLowerCase().trim();
    const filtered = validPlayers.filter(player => 
      player.displayName?.toLowerCase().includes(term) ||
      player.firstName?.toLowerCase().includes(term) ||
      player.lastName?.toLowerCase().includes(term) ||
      player.email?.toLowerCase().includes(term) ||
      player.city?.toLowerCase().includes(term) ||
      player.state?.toLowerCase().includes(term) ||
      player.country?.toLowerCase().includes(term) ||
      player.skillLevel?.toLowerCase().includes(term) ||
      player.preferredGameTypes?.some(game => game.toLowerCase().includes(term))
    );
    console.log(`[Friends] getFilteredPlayers: Returning ${filtered.length} players (filtered from ${validPlayers.length} with search: "${term}")`);
    return filtered;
  };

  getAvatar = (friend: Friend) => {
    return friend.avatar || friend.profilePicture || 'https://www.gravatar.com/avatar/00000000000000000000000000000000?d=mp&f=y&s=200';
  };

  render() {
    const {
      activeTab,
      searchTerm,
      friends,
      friendRequests,
      sentRequests,
      groups,
      loading,
      showCreateGroupModal,
      showAddFriendModal,
      selectedPlayer,
      friendRequestComment,
      newGroupName,
      newGroupDescription,
      newGroupColor
    } = this.state;

    return (
      <Fragment>
        <div className={styles.friendsContainer}>
        {/* Header */}
        <div className={styles.friendsHeader}>
          <h2 className={styles.title}>Connections & Groups</h2>
          <div className={styles.searchContainer}>
            <Search className={styles.searchIcon} size={20} />
            <input
              type="text"
              placeholder="Search connections or groups..."
              value={searchTerm}
              onChange={(e) => this.setState({ searchTerm: e.target.value })}
              className={styles.searchInput}
            />
          </div>
        </div>

        {/* Tabs */}
        <div className={styles.tabs}>
          <button
            className={`${styles.tab} ${activeTab === 'players' ? styles.active : ''}`}
            onClick={() => this.setState({ activeTab: 'players' })}
          >
            <Users size={18} />
            Find people
            {this.getFilteredPlayers().length > 0 && (
              <span className={styles.badge}>{this.getFilteredPlayers().length}</span>
            )}
          </button>
          <button
            className={`${styles.tab} ${activeTab === 'requests' ? styles.active : ''}`}
            onClick={() => this.setState({ activeTab: 'requests' })}
          >
            <UserPlus size={18} />
            Requests
            {(friendRequests.length > 0 || sentRequests.length > 0) && (
              <span className={styles.badge}>
                {friendRequests.length + sentRequests.length}
              </span>
            )}
          </button>
          <button
            className={`${styles.tab} ${activeTab === 'friends' ? styles.active : ''}`}
            onClick={() => this.setState({ activeTab: 'friends' })}
          >
            <Users size={18} />
            Connections
            {friends.length > 0 && (
              <span className={styles.badge}>{friends.length}</span>
            )}
          </button>
          <button
            className={`${styles.tab} ${activeTab === 'groups' ? styles.active : ''}`}
            onClick={() => this.setState({ activeTab: 'groups' })}
          >
            <Users size={18} />
            Groups
            {groups.length > 0 && (
              <span className={styles.badge}>{groups.length}</span>
            )}
          </button>
        </div>

        {/* Content */}
        <div className={styles.content}>
          {loading ? (
            <div className={styles.loading}>
              <div className={styles.spinner}></div>
              <p>Loading...</p>
            </div>
          ) : (
            <>
              {/* Players Tab */}
              {activeTab === 'players' && (
                <div className={styles.playersSection}>
                  <div className={styles.playersHeader}>
                    <h3 className={styles.sectionTitle}>
                      Public profiles ({this.getFilteredPlayers().length})
                    </h3>
                    <p className={styles.sectionSubtitle}>
                      People with public profiles — tap Connect to send a request
                    </p>
                  </div>
                  {(() => {
                    const filteredPlayers = this.getFilteredPlayers();
                    console.log(`[Friends] Render: About to render ${filteredPlayers.length} players`);
                    if (filteredPlayers.length > 0) {
                      // Check for duplicate keys
                      const ids = filteredPlayers.map(p => p.id);
                      const uniqueIds = new Set(ids);
                      if (ids.length !== uniqueIds.size) {
                        console.warn(`[Friends] Render: Found duplicate player IDs! Total: ${ids.length}, Unique: ${uniqueIds.size}`);
                        console.warn(`[Friends] Duplicate IDs:`, ids.filter((id, index) => ids.indexOf(id) !== index));
                      }
                    }
                    return filteredPlayers.length > 0 ? (
                      <div className={styles.playersGrid}>
                        {filteredPlayers.map((player, index) => (
                          <div key={`player-${player.id || player.userId}-${index}`} className={styles.playerCard}>
                          <div className={styles.playerCardHeader}>
                            <img
                              src={player.avatar || player.profilePicture || 'https://www.gravatar.com/avatar/00000000000000000000000000000000?d=mp&f=y&s=200'}
                              alt={player.displayName}
                              className={styles.playerAvatar}
                            />
                            <div className={styles.playerInfo}>
                              <h4 className={styles.playerName}>{player.displayName}</h4>
                              {player.tagline && (
                                <p className={styles.playerTagline}>{player.tagline}</p>
                              )}
                              <p className={styles.playerLocation}>
                                {[player.city, player.state].filter(Boolean).join(', ') || 'Location not set'}
                              </p>
                            </div>
                          </div>
                          {/* Skill, Rating, and Games sections hidden for now */}
                          {/* <div className={styles.playerCardBody}>
                            {player.skillLevel && (
                              <div className={styles.playerSkill}>
                                <span className={styles.skillLabel}>Skill:</span>
                                <span className={styles.skillBadge}>{player.skillLevel}</span>
                              </div>
                            )}
                            {player.rating && (
                              <div className={styles.playerRating}>
                                <span className={styles.ratingLabel}>Rating:</span>
                                <span className={styles.ratingValue}>{player.rating}</span>
                              </div>
                            )}
                            {player.preferredGameTypes && player.preferredGameTypes.length > 0 && (
                              <div className={styles.playerGames}>
                                <span className={styles.gamesLabel}>Games:</span>
                                <div className={styles.gamesTags}>
                                  {player.preferredGameTypes.map((game, idx) => (
                                    <span key={idx} className={styles.gameTag}>{game}</span>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div> */}
                          <div className={styles.playerCardFooter}>
                            <button
                              className={styles.addFriendButton}
                              onClick={() => this.handleOpenAddFriendModal(player)}
                              title="Send connection request"
                            >
                              <UserPlus size={16} />
                              Connect
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                    ) : (
                      <div className={styles.emptyState}>
                        <Users size={48} />
                        <p>No public players found matching your search.</p>
                      </div>
                    );
                  })()}
                </div>
              )}

              {/* Requests Tab */}
              {activeTab === 'requests' && (
                <div className={styles.requestsSection}>
                  {/* Received Requests */}
                  <div className={styles.requestSection}>
                    <h3 className={styles.sectionTitle}>
                      Received Requests ({friendRequests.length})
                    </h3>
                    {friendRequests.length > 0 ? (
                      <div className={styles.requestList}>
                        {friendRequests.map((request) => (
                          <div key={request.id} className={styles.requestCard}>
                            <img
                              src={this.getAvatar(request)}
                              alt={request.fullName}
                              className={styles.avatar}
                            />
                            <div className={styles.requestInfo}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.25rem' }}>
                                <h4>{request.fullName || `${request.firstName} ${request.lastName}`}</h4>
                                {request.status && (
                                  <span className={`${styles.statusBadge} ${styles[`status${request.status.charAt(0).toUpperCase() + request.status.slice(1)}`]}`}>
                                    {request.status.charAt(0).toUpperCase() + request.status.slice(1)}
                                  </span>
                                )}
                              </div>
                              <p className={styles.requestEmail}>{request.email}</p>
                              {request.skillLevel && (
                                <span className={styles.skillBadge}>{request.skillLevel}</span>
                              )}
                              {request.requestMessage && (
                                <div className={styles.requestMessage}>
                                  <strong>Message:</strong> {request.requestMessage}
                                </div>
                              )}
                              {request.status === 'accepted' && request.acceptComment && (
                                <div className={styles.acceptComment}>
                                  <strong>Acceptance Comment:</strong> {request.acceptComment}
                                </div>
                              )}
                              {request.status === 'rejected' && request.rejectComment && (
                                <div className={styles.rejectComment}>
                                  <strong>Rejection Comment:</strong> {request.rejectComment}
                                </div>
                              )}
                            </div>
                            <div className={styles.requestActions}>
                              {request.status === 'pending' ? (
                                <>
                                  <button
                                    className={styles.acceptButton}
                                    onClick={() => this.handleOpenAcceptModal(request)}
                                  >
                                    <Check size={16} />
                                    Accept
                                  </button>
                                  <button
                                    className={styles.rejectButton}
                                    onClick={() => this.handleOpenRejectModal(request)}
                                  >
                                    <X size={16} />
                                    Reject
                                  </button>
                                </>
                              ) : (
                                <div className={styles.statusInfo}>
                                  {request.status === 'accepted' && (
                                    <span className={styles.statusTextAccepted}>
                                      <Check size={16} />
                                      Accepted
                                    </span>
                                  )}
                                  {request.status === 'rejected' && (
                                    <span className={styles.statusTextRejected}>
                                      <X size={16} />
                                      Rejected
                                    </span>
                                  )}
                                  {request.status === 'cancelled' && (
                                    <span className={styles.statusTextCancelled}>
                                      <X size={16} />
                                      Cancelled
                                    </span>
                                  )}
                                </div>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className={styles.emptyState}>
                        <UserPlus size={48} />
                        <p>No pending connection requests</p>
                      </div>
                    )}
                  </div>

                  {/* Sent Requests */}
                  <div className={styles.requestSection}>
                    <h3 className={styles.sectionTitle}>
                      Sent Requests ({sentRequests.length})
                    </h3>
                    {sentRequests.length > 0 ? (
                      <div className={styles.requestList}>
                        {sentRequests.map((request) => (
                          <div key={request.id} className={styles.requestCard}>
                            <img
                              src={this.getAvatar(request)}
                              alt={request.fullName}
                              className={styles.avatar}
                            />
                            <div className={styles.requestInfo}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.25rem' }}>
                                <h4>{request.fullName || `${request.firstName} ${request.lastName}`}</h4>
                                {request.status && (
                                  <span className={`${styles.statusBadge} ${styles[`status${request.status.charAt(0).toUpperCase() + request.status.slice(1)}`]}`}>
                                    {request.status === 'sent' ? 'Pending' : request.status.charAt(0).toUpperCase() + request.status.slice(1)}
                                  </span>
                                )}
                              </div>
                              <p className={styles.requestEmail}>{request.email}</p>
                              {request.skillLevel && (
                                <span className={styles.skillBadge}>{request.skillLevel}</span>
                              )}
                              {request.requestMessage && (
                                <div className={styles.requestMessage}>
                                  <strong>Your Message:</strong> {request.requestMessage}
                                </div>
                              )}
                              {request.status === 'accepted' && request.acceptComment && (
                                <div className={styles.acceptComment}>
                                  <strong>Acceptance Comment:</strong> {request.acceptComment}
                                </div>
                              )}
                              {request.status === 'rejected' && request.rejectComment && (
                                <div className={styles.rejectComment}>
                                  <strong>Rejection Comment:</strong> {request.rejectComment}
                                </div>
                              )}
                            </div>
                            <div className={styles.requestActions}>
                              {request.status === 'pending' || request.status === 'sent' ? (
                                <button
                                  className={styles.cancelButton}
                                  onClick={() => this.handleCancelRequest(request.id)}
                                >
                                  <X size={16} />
                                  Cancel
                                </button>
                              ) : (
                                <div className={styles.statusInfo}>
                                  {request.status === 'accepted' && (
                                    <span className={styles.statusTextAccepted}>
                                      <Check size={16} />
                                      Accepted
                                    </span>
                                  )}
                                  {request.status === 'rejected' && (
                                    <span className={styles.statusTextRejected}>
                                      <X size={16} />
                                      Rejected
                                    </span>
                                  )}
                                  {request.status === 'cancelled' && (
                                    <span className={styles.statusTextCancelled}>
                                      <X size={16} />
                                      Cancelled
                                    </span>
                                  )}
                                </div>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className={styles.emptyState}>
                        <UserCheck size={48} />
                        <p>No pending sent requests</p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Friends Tab */}
              {activeTab === 'friends' && (
                <div className={styles.friendsSection}>
                  <div className={styles.friendsHeader}>
                    <h3 className={styles.sectionTitle}>
                      Connections ({this.getFilteredFriends().length})
                    </h3>
                  </div>
                  {this.getFilteredFriends().length > 0 ? (
                    <div className={styles.friendsGrid}>
                      {this.getFilteredFriends().map((friend) => {
                        const unreadCount = this.state.unreadCounts[friend.id] || 0;
                        return (
                          <div key={friend.id} className={styles.friendCard}>
                            <div className={styles.friendAvatarContainer}>
                              <img
                                src={this.getAvatar(friend)}
                                alt={friend.fullName}
                                className={styles.friendAvatar}
                              />
                              {unreadCount > 0 && (
                                <span className={styles.unreadBadge}>
                                  {unreadCount > 99 ? '99+' : unreadCount}
                                </span>
                              )}
                            </div>
                            <div className={styles.friendInfo}>
                              <h4>{friend.fullName || `${friend.firstName} ${friend.lastName}`}</h4>
                              <p className={styles.friendEmail}>{friend.email}</p>
                              {friend.skillLevel && (
                                <span className={styles.skillBadge}>{friend.skillLevel}</span>
                              )}
                            </div>
                            <div className={styles.friendActions}>
                              <button
                                className={styles.chatButton}
                                onClick={() => this.handleOpenChat(friend)}
                                title="Start Chat"
                              >
                                <MessageCircle size={16} />
                                {unreadCount > 0 && (
                                  <span className={styles.chatButtonBadge}></span>
                                )}
                              </button>
                              <button
                                className={styles.removeButton}
                                onClick={() => this.handleRemoveFriend(friend.id)}
                                title="Remove connection"
                              >
                                <UserX size={16} />
                              </button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className={styles.emptyState}>
                      <Users size={48} />
                      <p>No connections yet. Send a request to connect with people!</p>
                    </div>
                  )}
                </div>
              )}

              {/* Groups Tab */}
              {activeTab === 'groups' && (
                <div className={styles.groupsSection}>
                  <div className={styles.groupsHeader}>
                    <h3 className={styles.sectionTitle}>
                      Groups ({this.getFilteredGroups().length})
                    </h3>
                    <button
                      className={styles.createGroupButton}
                      onClick={() => this.setState({ showCreateGroupModal: true })}
                    >
                      <Plus size={18} />
                      Create Group
                    </button>
                  </div>
                  {this.getFilteredGroups().length > 0 ? (
                    <div className={styles.groupsGrid}>
                      {this.getFilteredGroups().map((group) => {
                        const userRole = this.state.userRoleInGroup[group.id] || 'member';
                        const isAdmin = userRole === 'admin';
                        return (
                          <div key={group.id} className={styles.groupCard}>
                            <div
                              className={styles.groupColor}
                              style={{ backgroundColor: group.color || '#3b82f6' }}
                            ></div>
                            {(group.profilePicUrl || group.avatarUrl) && (
                              <img
                                src={group.profilePicUrl || group.avatarUrl}
                                alt={group.name}
                                className={styles.groupAvatar}
                                style={{
                                  width: '60px',
                                  height: '60px',
                                  borderRadius: '50%',
                                  objectFit: 'cover',
                                  marginRight: '1rem',
                                  border: '2px solid #e5e7eb'
                                }}
                              />
                            )}
                            <div className={styles.groupContent}>
                              <h4>{group.name}</h4>
                              {group.description && (
                                <p className={styles.groupDescription}>{group.description}</p>
                              )}
                              <div className={styles.groupStats}>
                                <Users size={14} />
                                <span>{group.memberCount} members</span>
                                {isAdmin && (
                                  <span className={styles.roleBadge} style={{ marginLeft: '0.5rem', fontSize: '0.75rem', color: '#3b82f6' }}>
                                    Admin
                                  </span>
                                )}
                                {userRole === 'moderator' && (
                                  <span className={styles.roleBadge} style={{ marginLeft: '0.5rem', fontSize: '0.75rem', color: '#10b981' }}>
                                    Moderator
                                  </span>
                                )}
                              </div>
                            </div>
                            <div className={styles.groupActions}>
                              <button
                                className={styles.chatButton}
                                onClick={() => this.handleOpenGroupChat(group)}
                                title="Group Chat"
                                style={{ position: 'relative' }}
                              >
                                <MessageCircle size={16} />
                                {this.state.groupUnreadCounts[group.id] > 0 && (
                                  <span className={styles.chatButtonBadge}>
                                    {this.state.groupUnreadCounts[group.id] > 99 ? '99+' : this.state.groupUnreadCounts[group.id]}
                                  </span>
                                )}
                              </button>
                              {isAdmin && (
                                <button
                                  className={styles.deleteButton}
                                  onClick={() => this.handleDeleteGroup(group.id)}
                                  title="Delete Group"
                                >
                                  <Trash2 size={16} />
                                </button>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className={styles.emptyState}>
                      <Users size={48} />
                      <p>No groups yet. Create a group to organize your connections!</p>
                      <button
                        className={styles.createGroupButton}
                        onClick={() => this.setState({ showCreateGroupModal: true })}
                      >
                        <Plus size={18} />
                        Create Your First Group
                      </button>
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>

        {/* Create Group Modal */}
        {showCreateGroupModal && typeof document !== 'undefined' && createPortal(
          <div className={styles.modalOverlay} onClick={() => {
            // Clean up profile picture preview
            if (this.state.newGroupProfilePicPreview) {
              URL.revokeObjectURL(this.state.newGroupProfilePicPreview);
            }
            this.setState({ 
              showCreateGroupModal: false, 
              selectedFriendsForGroup: [],
              newGroupProfilePic: null,
              newGroupProfilePicPreview: null
            });
          }}>
            <div className={styles.modal} onClick={(e) => e.stopPropagation()} style={{ maxWidth: '600px', maxHeight: '90vh', overflow: 'auto' }}>
              <div className={styles.modalHeader}>
                <h3>Create Group</h3>
                <button
                  className={styles.closeButton}
                  onClick={() => {
                    // Clean up profile picture preview
                    if (this.state.newGroupProfilePicPreview) {
                      URL.revokeObjectURL(this.state.newGroupProfilePicPreview);
                    }
                    this.setState({ 
                      showCreateGroupModal: false, 
                      selectedFriendsForGroup: [],
                      newGroupProfilePic: null,
                      newGroupProfilePicPreview: null
                    });
                  }}
                >
                  <X size={20} />
                </button>
              </div>
              <div className={styles.modalContent}>
                <div className={styles.formGroup}>
                  <label>Group Name *</label>
                  <input
                    type="text"
                    value={newGroupName}
                    onChange={(e) => this.setState({ newGroupName: e.target.value })}
                    placeholder="Enter group name"
                    className={styles.formInput}
                  />
                </div>
                <div className={styles.formGroup}>
                  <label>Description</label>
                  <textarea
                    value={newGroupDescription}
                    onChange={(e) => this.setState({ newGroupDescription: e.target.value })}
                    placeholder="Enter group description (optional)"
                    className={styles.formTextarea}
                    rows={3}
                  />
                </div>
                <div className={styles.formGroup}>
                  <label>Profile Picture (Optional)</label>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                    {this.state.newGroupProfilePicPreview && (
                      <img
                        src={this.state.newGroupProfilePicPreview}
                        alt="Profile preview"
                        style={{
                          width: '80px',
                          height: '80px',
                          borderRadius: '50%',
                          objectFit: 'cover',
                          border: '2px solid #e5e7eb'
                        }}
                      />
                    )}
                    <div style={{ flex: 1 }}>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (file) {
                            // Validate file size (max 10MB)
                            if (file.size > 10 * 1024 * 1024) {
                              alert('Profile picture must be less than 10MB');
                              e.target.value = '';
                              return;
                            }
                            // Validate file type
                            if (!file.type.startsWith('image/')) {
                              alert('Please select an image file');
                              e.target.value = '';
                              return;
                            }
                            // Clean up previous preview
                            if (this.state.newGroupProfilePicPreview) {
                              URL.revokeObjectURL(this.state.newGroupProfilePicPreview);
                            }
                            // Create preview
                            const preview = URL.createObjectURL(file);
                            this.setState({
                              newGroupProfilePic: file,
                              newGroupProfilePicPreview: preview
                            });
                          }
                        }}
                        style={{ display: 'none' }}
                        id="group-profile-pic-input"
                      />
                      <label
                        htmlFor="group-profile-pic-input"
                        style={{
                          display: 'inline-block',
                          padding: '0.5rem 1rem',
                          backgroundColor: '#f3f4f6',
                          border: '1px solid #d1d5db',
                          borderRadius: '6px',
                          cursor: 'pointer',
                          fontSize: '0.875rem'
                        }}
                      >
                        {this.state.newGroupProfilePic ? 'Change Picture' : 'Choose Picture'}
                      </label>
                      {this.state.newGroupProfilePic && (
                        <button
                          type="button"
                          onClick={() => {
                            if (this.state.newGroupProfilePicPreview) {
                              URL.revokeObjectURL(this.state.newGroupProfilePicPreview);
                            }
                            this.setState({
                              newGroupProfilePic: null,
                              newGroupProfilePicPreview: null
                            });
                            const input = document.getElementById('group-profile-pic-input') as HTMLInputElement;
                            if (input) input.value = '';
                          }}
                          style={{
                            marginLeft: '0.5rem',
                            padding: '0.25rem 0.5rem',
                            backgroundColor: '#ef4444',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            fontSize: '0.75rem'
                          }}
                        >
                          Remove
                        </button>
                      )}
                    </div>
                  </div>
                </div>
                <div className={styles.formGroup}>
                  <label>Color</label>
                  <div className={styles.colorPicker}>
                    {['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899'].map((color) => (
                      <button
                        key={color}
                        className={`${styles.colorOption} ${newGroupColor === color ? styles.selected : ''}`}
                        style={{ backgroundColor: color }}
                        onClick={() => this.setState({ newGroupColor: color })}
                      />
                    ))}
                  </div>
                </div>
                <div className={styles.formGroup}>
                  <label>Select connections to invite *</label>
                  <div className={styles.friendsSelectionList} style={{ maxHeight: '300px', overflowY: 'auto', border: '1px solid #e5e7eb', borderRadius: '8px', padding: '0.5rem' }}>
                    {this.getFilteredFriends().length > 0 ? (
                      this.getFilteredFriends().map((friend) => {
                        const isSelected = this.state.selectedFriendsForGroup.includes(friend.id);
                        return (
                          <div
                            key={friend.id}
                            className={styles.friendSelectionItem}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '0.75rem',
                              padding: '0.75rem',
                              borderRadius: '6px',
                              cursor: 'pointer',
                              backgroundColor: isSelected ? '#e0f2fe' : 'transparent',
                              border: isSelected ? '2px solid #3b82f6' : '1px solid transparent',
                              marginBottom: '0.5rem',
                              transition: 'all 0.2s'
                            }}
                            onClick={() => this.handleToggleFriendSelection(friend.id)}
                          >
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onChange={() => this.handleToggleFriendSelection(friend.id)}
                              style={{ cursor: 'pointer' }}
                            />
                            <img
                              src={this.getAvatar(friend)}
                              alt={friend.fullName}
                              style={{ width: '40px', height: '40px', borderRadius: '50%', objectFit: 'cover' }}
                            />
                            <div style={{ flex: 1 }}>
                              <div style={{ fontWeight: 500 }}>{friend.fullName || `${friend.firstName} ${friend.lastName}`}</div>
                              <div style={{ fontSize: '0.875rem', color: '#6b7280' }}>{friend.email}</div>
                            </div>
                          </div>
                        );
                      })
                    ) : (
                      <div style={{ padding: '2rem', textAlign: 'center', color: '#6b7280' }}>
                        <Users size={32} style={{ marginBottom: '0.5rem', opacity: 0.5 }} />
                        <p>No connections yet. Connect with people first to create a group.</p>
                      </div>
                    )}
                  </div>
                  {this.state.selectedFriendsForGroup.length > 0 && (
                    <div style={{ marginTop: '0.5rem', fontSize: '0.875rem', color: '#059669' }}>
                      {this.state.selectedFriendsForGroup.length} friend{this.state.selectedFriendsForGroup.length !== 1 ? 's' : ''} selected
                    </div>
                  )}
                </div>
              </div>
              <div className={styles.modalFooter}>
                <button
                  className={styles.cancelButton}
                  onClick={() => {
                    // Clean up profile picture preview
                    if (this.state.newGroupProfilePicPreview) {
                      URL.revokeObjectURL(this.state.newGroupProfilePicPreview);
                    }
                    this.setState({ 
                      showCreateGroupModal: false, 
                      selectedFriendsForGroup: [],
                      newGroupProfilePic: null,
                      newGroupProfilePicPreview: null
                    });
                  }}
                >
                  Cancel
                </button>
                <button
                  className={styles.createButton}
                  onClick={this.handleCreateGroup}
                  disabled={this.state.selectedFriendsForGroup.length === 0}
                >
                  Create Group ({this.state.selectedFriendsForGroup.length})
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}

        {/* Add Group Members Modal */}
        {this.state.showAddMembersModal && this.state.selectedGroupForAddMembers && typeof document !== 'undefined' && createPortal(
          <div className={styles.modalOverlay} onClick={this.handleCloseAddMembersModal}>
            <div className={styles.modal} onClick={(e) => e.stopPropagation()} style={{ maxWidth: '500px', maxHeight: '90vh', overflow: 'auto' }}>
              <div className={styles.modalHeader}>
                <h3>Add members to {this.state.selectedGroupForAddMembers.name}</h3>
                <button type="button" className={styles.closeButton} onClick={this.handleCloseAddMembersModal}>
                  <X size={20} />
                </button>
              </div>
              <div className={styles.modalContent}>
                <p style={{ marginBottom: '1rem', fontSize: '0.875rem', color: '#6b7280' }}>
                  Select friends to add. Only friends who are not already in the group are shown.
                </p>
                <div className={styles.formGroup}>
                  <label>Select connections to add</label>
                  <div className={styles.friendsSelectionList} style={{ maxHeight: '300px', overflowY: 'auto', border: '1px solid #e5e7eb', borderRadius: '8px', padding: '0.5rem' }}>
                    {this.getFriendsNotInGroup().length > 0 ? (
                      this.getFriendsNotInGroup().map((friend) => {
                        const isSelected = this.state.selectedFriendIdsToAdd.includes(friend.id);
                        return (
                          <div
                            key={friend.id}
                            className={styles.friendSelectionItem}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '0.75rem',
                              padding: '0.75rem',
                              borderRadius: '6px',
                              cursor: 'pointer',
                              backgroundColor: isSelected ? '#e0f2fe' : 'transparent',
                              border: isSelected ? '2px solid #3b82f6' : '1px solid transparent',
                              marginBottom: '0.5rem',
                              transition: 'all 0.2s'
                            }}
                            onClick={() => this.handleToggleAddMemberFriend(friend.id)}
                          >
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onChange={() => this.handleToggleAddMemberFriend(friend.id)}
                              style={{ cursor: 'pointer' }}
                            />
                            <img
                              src={this.getAvatar(friend)}
                              alt={friend.fullName}
                              style={{ width: '40px', height: '40px', borderRadius: '50%', objectFit: 'cover' }}
                            />
                            <div style={{ flex: 1 }}>
                              <div style={{ fontWeight: 500 }}>{friend.fullName || `${friend.firstName} ${friend.lastName}`}</div>
                              <div style={{ fontSize: '0.875rem', color: '#6b7280' }}>{friend.email}</div>
                            </div>
                          </div>
                        );
                      })
                    ) : (
                      <div style={{ padding: '2rem', textAlign: 'center', color: '#6b7280' }}>
                        <Users size={32} style={{ marginBottom: '0.5rem', opacity: 0.5 }} />
                        <p>No connections available to add. All your connections are already in this group.</p>
                      </div>
                    )}
                  </div>
                  {this.state.selectedFriendIdsToAdd.length > 0 && (
                    <div style={{ marginTop: '0.5rem', fontSize: '0.875rem', color: '#059669' }}>
                      {this.state.selectedFriendIdsToAdd.length} friend{this.state.selectedFriendIdsToAdd.length !== 1 ? 's' : ''} selected
                    </div>
                  )}
                </div>
              </div>
              <div className={styles.modalFooter}>
                <button type="button" className={styles.cancelButton} onClick={this.handleCloseAddMembersModal}>
                  Cancel
                </button>
                <button
                  type="button"
                  className={styles.createButton}
                  onClick={this.handleAddGroupMembers}
                  disabled={this.state.selectedFriendIdsToAdd.length === 0}
                >
                  Add members ({this.state.selectedFriendIdsToAdd.length})
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}

        {/* Add Friend Modal - Rendered via Portal */}
        {showAddFriendModal && selectedPlayer && typeof document !== 'undefined' && createPortal(
          <div className={styles.modalOverlay} onClick={this.handleCloseAddFriendModal}>
            <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
              <div className={styles.modalHeader}>
                <h3>Send connection request</h3>
                <button
                  className={styles.closeButton}
                  onClick={this.handleCloseAddFriendModal}
                >
                  <X size={20} />
                </button>
              </div>
              <div className={styles.modalContent}>
                <div className={styles.friendRequestPreview}>
                  <img
                    src={selectedPlayer.avatar || selectedPlayer.profilePicture || 'https://www.gravatar.com/avatar/00000000000000000000000000000000?d=mp&f=y&s=200'}
                    alt={selectedPlayer.displayName}
                    className={styles.previewAvatar}
                  />
                  <div className={styles.previewInfo}>
                    <h4>{selectedPlayer.displayName}</h4>
                    <p className={styles.previewLocation}>
                      {[selectedPlayer.city, selectedPlayer.state].filter(Boolean).join(', ') || 'Location not set'}
                    </p>
                  </div>
                </div>
                <div className={styles.formGroup}>
                  <label>Add a message (optional)</label>
                  <textarea
                    value={friendRequestComment}
                    onChange={(e) => this.setState({ friendRequestComment: e.target.value })}
                    placeholder="Hi! I'd like to connect..."
                    className={styles.formTextarea}
                    rows={4}
                    maxLength={500}
                  />
                  <div className={styles.characterCount}>
                    {friendRequestComment.length}/500
                  </div>
                </div>
              </div>
              <div className={styles.modalFooter}>
                <button
                  className={styles.cancelButton}
                  onClick={this.handleCloseAddFriendModal}
                >
                  Cancel
                </button>
                <button
                  className={styles.createButton}
                  onClick={this.handleConfirmAddFriend}
                >
                  <UserPlus size={16} />
                  Send Request
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}

        {/* Accept Friend Request Modal */}
        {this.state.showAcceptModal && this.state.selectedRequest && (
          <div className={styles.modalOverlay} onClick={this.handleCloseAcceptModal}>
            <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
              <div className={styles.modalHeader}>
                <h3>Accept connection request</h3>
                <button
                  className={styles.closeButton}
                  onClick={this.handleCloseAcceptModal}
                >
                  <X size={20} />
                </button>
              </div>
              <div className={styles.modalContent}>
                <div className={styles.friendRequestPreview}>
                  <img
                    src={this.state.selectedRequest.avatar || this.state.selectedRequest.profilePicture || 'https://www.gravatar.com/avatar/00000000000000000000000000000000?d=mp&f=y&s=200'}
                    alt={this.state.selectedRequest.fullName}
                    className={styles.previewAvatar}
                  />
                  <div className={styles.previewInfo}>
                    <h4>{this.state.selectedRequest.fullName}</h4>
                    <p>{this.state.selectedRequest.email}</p>
                  </div>
                </div>
                <div className={styles.formGroup}>
                  <label>Add a comment (optional)</label>
                  <textarea
                    value={this.state.acceptComment}
                    onChange={(e) => this.setState({ acceptComment: e.target.value })}
                    placeholder="Add a friendly message..."
                    className={styles.formTextarea}
                    maxLength={500}
                    rows={4}
                  />
                  <div className={styles.characterCount}>
                    {this.state.acceptComment.length}/500
                  </div>
                </div>
              </div>
              <div className={styles.modalFooter}>
                <button
                  className={styles.cancelButton}
                  onClick={this.handleCloseAcceptModal}
                  disabled={this.state.loading}
                >
                  Cancel
                </button>
                <button
                  className={styles.acceptButton}
                  onClick={this.handleConfirmAccept}
                  disabled={this.state.loading}
                >
                  <Check size={16} />
                  {this.state.loading ? 'Accepting...' : 'Accept Request'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Reject Friend Request Modal */}
        {this.state.showRejectModal && this.state.selectedRequest && (
          <div className={styles.modalOverlay} onClick={this.handleCloseRejectModal}>
            <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
              <div className={styles.modalHeader}>
                <h3>Decline connection request</h3>
                <button
                  className={styles.closeButton}
                  onClick={this.handleCloseRejectModal}
                >
                  <X size={20} />
                </button>
              </div>
              <div className={styles.modalContent}>
                <div className={styles.friendRequestPreview}>
                  <img
                    src={this.state.selectedRequest.avatar || this.state.selectedRequest.profilePicture || 'https://www.gravatar.com/avatar/00000000000000000000000000000000?d=mp&f=y&s=200'}
                    alt={this.state.selectedRequest.fullName}
                    className={styles.previewAvatar}
                  />
                  <div className={styles.previewInfo}>
                    <h4>{this.state.selectedRequest.fullName}</h4>
                    <p>{this.state.selectedRequest.email}</p>
                  </div>
                </div>
                <div className={styles.formGroup}>
                  <label>Add a comment (optional)</label>
                  <textarea
                    value={this.state.rejectComment}
                    onChange={(e) => this.setState({ rejectComment: e.target.value })}
                    placeholder="Add a reason for rejection (optional)..."
                    className={styles.formTextarea}
                    maxLength={500}
                    rows={4}
                  />
                  <div className={styles.characterCount}>
                    {this.state.rejectComment.length}/500
                  </div>
                </div>
              </div>
              <div className={styles.modalFooter}>
                <button
                  className={styles.cancelButton}
                  onClick={this.handleCloseRejectModal}
                  disabled={this.state.loading}
                >
                  Cancel
                </button>
                <button
                  className={styles.rejectButton}
                  onClick={this.handleConfirmReject}
                  disabled={this.state.loading}
                >
                  <X size={16} />
                  {this.state.loading ? 'Rejecting...' : 'Reject Request'}
                </button>
              </div>
            </div>
          </div>
        )}

        </div>
        {/* Friend Chat - Full-screen popup overlay */}
        {this.state.showChat && this.state.selectedFriendForChat && this.state.currentUserId && typeof document !== 'undefined' && createPortal(
          (() => {
            const friend = this.state.selectedFriendForChat!;
            return (
              <div
                className={styles.chatOverlay}
                onClick={(e) => { if (e.target === e.currentTarget) this.handleCloseChat(); }}
                role="dialog"
                aria-modal="true"
                aria-label="Chat with connection"
              >
                <div className={styles.chatOverlayContent} onClick={(e) => e.stopPropagation()}>
                  <Chat
                    key={friend.id}
                    ref={this.chatRef}
                    embedded
                    friend={{
                      id: friend.id,
                      userId: friend.userId,
                      fullName: friend.fullName || `${friend.firstName || ''} ${friend.lastName || ''}`.trim() || 'Unknown',
                      email: friend.email || '',
                      avatar: friend.avatar,
                      profilePicture: friend.profilePicture
                    }}
                    currentUserId={this.state.currentUserId!}
                    currentUserProfileId={this.state.currentUserProfileId!}
                    onClose={this.handleCloseChat}
                    onSendMessage={this.handleSendMessage}
                    onLoadMessages={this.handleLoadMessages}
                    onMarkAsRead={this.handleMarkAsRead}
                    isFriendTyping={this.state.isFriendTyping}
                    onTyping={this.handleTyping}
                  />
                </div>
              </div>
            );
          })(),
          document.body
        )}

        {/* Group Chat */}
        {this.state.showGroupChat && this.state.selectedGroupForChat && this.state.currentUserId && this.state.currentUserProfileId && (() => {
          const group = this.state.selectedGroupForChat!;
          const members = this.state.groupMembers[group.id] || [];
          const userRole = this.state.userRoleInGroup[group.id] || 'member';
          
          return (
            <GroupChat
              ref={this.groupChatRef}
              group={{
                id: group.id,
                name: group.name,
                description: group.description,
                profilePicUrl: group.profilePicUrl || group.avatarUrl || group.profile_pic_url
              }}
              currentUserId={this.state.currentUserId!}
              currentUserProfileId={this.state.currentUserProfileId!}
              currentUserRole={userRole}
              onClose={this.handleCloseGroupChat}
              onAddMembers={this.handleOpenAddMembersModal}
              onSendMessage={this.handleSendGroupMessage}
              onLoadMessages={this.handleLoadGroupMessages}
              members={members.map((member: any) => ({
                id: member.id || member.profileId,
                userId: member.userId,
                displayName: member.displayName || member.fullName || `${member.firstName || ''} ${member.lastName || ''}`.trim() || 'Unknown',
                profilePic: member.profilePic || member.profilePicture || member.avatar,
                role: member.role
              }))}
            />
          );
        })()}
      </Fragment>
    );
  }
}

export default Friends;

